import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import {
  searchSuburbOptions,
  searchSuburbOptionsByPostCode,
  type SuburbLookupRow,
} from '@/services/suburbLookup';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { formatSuburbLabel } from '@/utils/suburbText';
import { formatUserFacingError } from '@/utils/userFacingErrors';
import { Button, Card, Modal, StatusBadge } from '@/ui/components';
import { resolveCertificateEngineerNameForUser } from './certificateEngineerDefaults';
import CertificateProvisionHistory from './CertificateProvisionHistory';
import CertificateHistoryLookup from './CertificateHistoryLookup';
import './workflowDataGridFocus.css';

type WorkflowMode = 'create' | 'amend';

type ProjectOption = {
  ProjectId: number;
  ProjectCode?: string;
  ProjectName?: string;
  SiteAddress?: string;
  Suburb?: string;
  PostCode?: number | string | null;
  Council?: string | null;
};

type CertificateOption = {
  CertificateId?: number;
  CertificateNumber?: string;
  ProjectNumber?: string;
  ProjectLocation?: string;
  CertificateType?: string;
  IssueDate?: string;
  Status?: string;
};

type CertificatePractitionerOption = {
  name: string;
  email: string | null;
  address: string | null;
  company: string | null;
  registrationCategoryClass: string | null;
  registrationNumber: string | null;
  sourceTable: string | null;
};

type CertificateBuildingSurveyorOption = {
  name: string;
  email: string | null;
  postalAddress: string | null;
  postcode: string | null;
  sourceTable: string | null;
};

type BuildingClassificationRow = {
  partOfBuilding: string;
  bcaClassification: string;
};

type BuildingClassificationOption = {
  partOfBuilding: string;
  bcaClassification: string;
  sourceTable?: string | null;
  isDefault?: boolean;
};

type BuildingClassificationHelperOption = BuildingClassificationOption & {
  aliases?: string[];
  helperNote?: string;
};

type DocumentRow = {
  section: string;
  documentNo: string;
  documentDate: string;
  documentType: string;
  numberOfPages: string;
  preparedBy: string;
};

type DrawingDocNoGeneratorSettings = {
  prefix: string;
  totalPages: string;
  revisionSequenceInput: string;
};

type PerformanceRequirementRow = {
  relevantPerformanceRequirement: string;
  details: string;
};

type NccReferenceRow = {
  version: string;
};

type ProvisionRow = {
  sectionProvision: string;
};

type StoredDraftPdf = {
  renderId?: number | null;
  versionNo?: number | null;
  draftFileId?: string | null;
  fileName?: string | null;
  contentPath?: string | null;
  contentType?: string | null;
  uploadedBy?: string | null;
  createdDate?: string | null;
};

type CertificateAustralianStandardsAutoFillResponse = {
  standards?: string[];
  reasons?: string[];
  bcaVersion?: string;
};

type DocumentApprovalState = {
  approvalId: string;
  status: string;
  approverEmail?: string | null;
  expiresAtLabel?: string | null;
  approvedAt?: string | null;
  pdfBase64: string;
  pdfFileName: string;
  pdfContentType: string;
};

type CertificateDraftResponse = {
  source?: 'current' | 'civil';
  certificateId?: number;
  certificateNumber?: string;
  projectId?: number | null;
  projectNumber?: string;
  projectFolderName?: string | null;
  projectAddress?: string;
  projectSuburb?: string;
  projectPostcode?: string;
  projectCouncil?: string;
  title?: string;
  certificateType?: string;
  issueDate?: string;
  status?: string;
  notes?: string;
  versionTag?: string;
  latestVersionNo?: number | null;
  snapshot?: Record<string, unknown>;
  storedDraftPdf?: StoredDraftPdf | null;
};

type CertificateEditorForm = {
  certificatePurpose: 'design' | 'inspection';
  inspectionWorkClass: string;
  inspectionDate: string;
  buildingWorkInspected: string;
  inspectionFindings: string;
  inspectionLimitations: string;
  effectiveHeight: string;
  declarationInspectedPersonally: boolean;
  declarationInspectedWorkComplies: boolean;
  certificateId: string;
  certificateNumber: string;
  projectId: string;
  projectNumber: string;
  projectFolderName: string;
  title: string;
  certificateType: string;
  issueDate: string;
  status: string;
  notes: string;
  versionTag: string;
  issuedToEntity: string;
  buildingSurveyor: string;
  buildingSurveyorEmail: string;
  buildingSurveyorPostalAddress: string;
  buildingSurveyorPostcode: string;
  projectAddress: string;
  projectSuburb: string;
  projectPostcode: string;
  projectCouncil: string;
  natureOfBuildingWorkType: string;
  natureOfBuildingWorkDescription: string;
  storeysContained: string;
  riseInStoreys: string;
  typeOfConstruction: string;
  bcaVersion: string;
  matterType: string;
  engineerName: string;
  engineerEmail: string;
  engineerAddress: string;
  engineerRegistrationCategoryClass: string;
  engineerRegistrationNumber: string;
  engineerIssueDate: string;
  includePerformanceSolution: boolean;
  declarationPreparedDesign: boolean;
  declarationDesignComplies: boolean;
  declarationPerformanceSolutionComplies: boolean;
  declarationCompetent: boolean;
  buildingClassifications: BuildingClassificationRow[];
  documents: DocumentRow[];
  performanceRequirements: PerformanceRequirementRow[];
  nccReferences: NccReferenceRow[];
  provisions: ProvisionRow[];
};

const STRUCTURAL_MATTER = 'Structural Matter';
const DRAINAGE_MATTER = 'Drainage Matter';
const matterTypeOptions = [STRUCTURAL_MATTER, DRAINAGE_MATTER];
const proposedCertificateRenderStyle = 'proposed-building-work';
const typeOfConstructionOptions = ['', 'A', 'B', 'C'];
const natureOfBuildingWorkTypeOptions = [
  '',
  'New Building',
  'Extension',
  'Alteration',
  'Change of Use',
  'Demolition',
  'Removal',
  'Re-erection of a building',
];

const inputBase: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgba(144, 163, 195, 0.34)',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 15,
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.98)',
  color: '#10243f',
};

const sheetInput: React.CSSProperties = {
  width: '100%',
  border: '1px solid transparent',
  background: 'transparent',
  padding: 0,
  outline: 'none',
  fontSize: 15,
  color: '#0f172a',
  fontWeight: 600,
};

const sheetTextArea: React.CSSProperties = {
  ...sheetInput,
  resize: 'vertical',
  minHeight: 54,
  lineHeight: 1.45,
  fontWeight: 500,
};

const sheetLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#34579e',
  marginBottom: 8,
};

const tableHeaderCell: React.CSSProperties = {
  padding: '10px 9px',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#fff',
  background: '#234ea8',
  borderRight: '1px solid rgba(255,255,255,0.25)',
};

const tableCell: React.CSSProperties = {
  padding: '8px 9px',
  borderTop: '1px solid rgba(144, 163, 195, 0.2)',
  borderRight: '1px solid rgba(144, 163, 195, 0.2)',
  background: '#fff',
};

const pageButtonBase: React.CSSProperties = {
  minHeight: 'var(--button-min-height-md)',
  padding: 'var(--button-padding-sm-y) var(--button-padding-sm-x)',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.06em',
  lineHeight: 1.1,
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const pageButtonPrimary: React.CSSProperties = {
  ...pageButtonBase,
  border: 'none',
  background: 'linear-gradient(135deg, #234ea8 0%, #2f6fd4 100%)',
  color: '#fff',
};

const pageButtonSecondary: React.CSSProperties = {
  ...pageButtonBase,
  border: '1px solid rgba(144, 163, 195, 0.3)',
  background: '#fff',
  color: '#31435d',
};

const pageButtonDark: React.CSSProperties = {
  ...pageButtonBase,
  border: 'none',
  background: '#35527f',
  color: '#fff',
};

const pageButtonSuccess: React.CSSProperties = {
  ...pageButtonBase,
  border: 'none',
  background: '#1f7a4d',
  color: '#fff',
};

const pageButtonAddRow: React.CSSProperties = {
  ...pageButtonBase,
  border: '1px dashed rgba(47, 111, 212, 0.55)',
  background: 'rgba(234, 241, 255, 0.96)',
  color: '#234ea8',
};

const pageButtonTeal: React.CSSProperties = {
  ...pageButtonBase,
  border: '1px solid rgba(35, 78, 168, 0.34)',
  background: 'rgba(234, 241, 255, 0.96)',
  color: '#234ea8',
};

const pageButtonDanger: React.CSSProperties = {
  ...pageButtonBase,
  border: 'none',
  background: 'rgba(190,24,93,0.1)',
  color: '#be185d',
};

const pageButtonTopNav: React.CSSProperties = {
  ...pageButtonBase,
  minHeight: 36,
  border: '1px solid rgba(255,255,255,0.32)',
  background: 'rgba(255,255,255,0.12)',
  color: '#fff',
};

const statusOptions = ['Draft', 'Pending', 'Issued', 'Expired'];
const nccProvisionOptions = [
  'National Construction Code 2022 Volume One (Class 2-9)',
  'National Construction Code 2022 Volume 2',
  'National Construction Code 2022 Volume Three (Drainage)',
  'National Construction Code 2025 Volume One (Class 2-9)',
  'National Construction Code 2025 Volume 2',
  'National Construction Code 2025 Volume Three (Drainage)',
];
const defaultAustralianStandardOptions = [
  'AS1170.0 Structural Design Actions - Part 0: General principles',
  'AS1170.1 Structural Design Actions - Part 1: Permanent, imposed and other actions',
  'AS1170.2 Structural Design Actions - Part 2: Wind actions',
  'AS1170.3 Structural Design Actions - Part 3: Snow and ice actions',
  'AS1170.4 Structural Design Actions - Part 4: Earthquake loads',
  'AS1684.1 Residential timber-framed construction - Part 1: Design criteria',
  'AS1684.2 Residential timber-framed construction - Part 2: Non-cyclonic areas',
  'AS1684.4 Residential timber-framed construction - Part 4: Simplified non-cyclonic areas',
  'AS1720.1 Timber structures - Part 1: Design methods',
  'AS1720.2 Timber structures - Part 2: Timber properties',
  'AS1720.4 Timber structures - Part 4: Fire-resistance of structural timber members',
  'AS2870 Residential slabs and footings',
  'AS3600 Concrete structures (2018)',
  'AS3610 Formwork of concrete',
  'AS3700 Masonry structures',
  'AS3735 Concrete structures for retaining liquids',
  'AS3850 Tilt-up concrete construction',
  'AS3959 Construction of building in bushfire-prone areas',
  'AS4100 Steel structures',
  'AS4678 Earth-retaining structures',
  'referenced therein including AS/NZS 3500.3.- 2015,',
  'Plumbing Industry Solutions - Technical Solutions',
  'Council Standard Drawings',
  'AS4600 Cold-formed Steel Structures',
  'NASH Standards',
];
const australianStandardStorageKey = 'certificate-workflow-australian-standards';
const supportingDocumentSectionOptions = ['Drawing', 'Computation', 'Documents'];
const preparedByOptions = [
  'Linfinity Engineering Pty Ltd',
  'Equilibrium Engineering Pty Ltd',
];
const fallbackPreparedBy = preparedByOptions[0];

const looksLikePractitionerNamePlaceholder = (value: string | null | undefined, company = '') => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  const normalizedCompany = String(company || '').trim().toLowerCase();
  if (!normalizedValue) return true;
  if (normalizedValue.includes('@')) return true;
  if (normalizedCompany && normalizedValue === normalizedCompany) return true;
  if (/[0-9,]/.test(normalizedValue)) return true;
  if (/\b(pty ltd|engineering|road|street|st\b|avenue|ave\b|place|vic\b|suite|tenancy|level|unit|po box)\b/.test(normalizedValue)) return true;
  return false;
};

const resolvePractitionerAddress = (address: string | null | undefined, company: string | null | undefined) => {
  const normalizedAddress = String(address || '').trim();
  const normalizedCompany = String(company || '').trim();
  if (normalizedAddress && normalizedAddress.toLowerCase() !== normalizedCompany.toLowerCase()) {
    return normalizedAddress;
  }
  return normalizedAddress || '';
};

const resolvePractitionerRegistrationCategoryClass = (registrationCategoryClass: string | null | undefined) => {
  const normalizedCategory = String(registrationCategoryClass || '').trim();
  return normalizedCategory || '';
};

const structuralRevisionSequenceDefault = 'A,B,C,D,E,0,1,2,3,4,5,6,7,8,9';
const civilRevisionSequenceDefault = '0,1,2,3,4,5,A,B,C,D,E,F,G';
const isStructuralMatter = (matterType: string) => !isDrainageMatter(matterType);
const normalizeMatterTypeLabel = (value: unknown) => {
  const normalized = String(value || '').trim();
  if (!normalized) return STRUCTURAL_MATTER;
  return isDrainageMatter(normalized) ? DRAINAGE_MATTER : STRUCTURAL_MATTER;
};
const nccBuildingClassificationHelperOptions: BuildingClassificationHelperOption[] = [
  {
    partOfBuilding: 'House',
    bcaClassification: '1a',
    aliases: ['detached house', 'single dwelling'],
    helperNote: 'Class 1a covers detached houses and similar single dwellings.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Town house / row house / duplex',
    bcaClassification: '1a',
    aliases: ['town house', 'townhouse', 'row house', 'terrace house', 'duplex', 'attached dwelling'],
    helperNote: 'Class 1a also covers attached dwellings such as town houses and duplexes.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Boarding house / guest house / hostel (small)',
    bcaClassification: '1b',
    aliases: ['small boarding house', 'small guest house', 'small hostel', 'short-term holiday accommodation'],
    helperNote: 'Use Class 1b only for the small NCC limits; larger accommodation buildings move to Class 3.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Apartment building / flat',
    bcaClassification: '2',
    aliases: ['apartment', 'apartments', 'flat', 'flats', 'units', 'residential apartment'],
    helperNote: 'Class 2 is for apartment-style residential buildings.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Residential accommodation (larger shared living)',
    bcaClassification: '3',
    aliases: ['backpackers', 'dormitory', 'workers quarters', 'workers quarter', 'large boarding house', 'large hostel'],
    helperNote: 'Class 3 applies to larger shared or transient residential accommodation.',
    isDefault: true,
  },
  {
    partOfBuilding: "Caretaker's residence",
    bcaClassification: '4',
    aliases: ['caretaker residence', 'sole residence in non-residential building'],
    helperNote: 'Class 4 is the single dwelling inside a non-residential Class 5-9 building.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Office',
    bcaClassification: '5',
    aliases: ['commercial office', 'professional office', "medical practitioner's office", 'architect office'],
    helperNote: 'Class 5 is for office buildings used for professional or commercial purposes.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Shop / retail / showroom',
    bcaClassification: '6',
    aliases: ['shop', 'retail', 'showroom', 'market', 'public laundry', 'hairdresser', 'barber'],
    helperNote: 'Class 6 covers retail shops and services supplied directly to the public.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Restaurant / cafe / bar / kiosk',
    bcaClassification: '6',
    aliases: ['restaurant', 'cafe', 'bar', 'kiosk', 'dining room'],
    helperNote: 'Class 6 also covers restaurants, cafes, bars, and kiosks open to the public.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Service station',
    bcaClassification: '6',
    aliases: ['fuel station', 'petrol station'],
    helperNote: 'Service stations are generally Class 6 under the NCC guide.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Carpark',
    bcaClassification: '7a',
    aliases: ['car park', 'parking garage'],
    helperNote: 'Class 7a is for carparks.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Warehouse / storage / wholesale display',
    bcaClassification: '7b',
    aliases: ['warehouse', 'storage building', 'storage', 'wholesale display'],
    helperNote: 'Class 7b is for storage buildings and wholesale display uses.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Factory / workshop',
    bcaClassification: '8',
    aliases: ['factory', 'workshop', "mechanic's workshop", 'mechanic workshop', 'abattoir'],
    helperNote: 'Class 8 is for buildings where a process or handicraft is carried out for trade, sale, or gain.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Hospital / health-care building / day surgery',
    bcaClassification: '9a',
    aliases: ['hospital', 'health care building', 'health-care building', 'day surgery'],
    helperNote: 'Class 9a is for health-care buildings where occupants may need assistance to evacuate.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Assembly / education / childcare / sporting facility',
    bcaClassification: '9b',
    aliases: ['assembly building', 'school', 'university', 'childcare centre', 'child care centre', 'preschool', 'pre-school', 'sporting facility', 'night club', 'public transport building'],
    helperNote: 'Class 9b is for assembly buildings such as schools, childcare, and sporting facilities.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Residential care building / aged care',
    bcaClassification: '9c',
    aliases: ['residential care building', 'aged care', 'aged care building'],
    helperNote: 'Class 9c is for residential care buildings where residents need physical assistance.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Shed / carport / private garage',
    bcaClassification: '10a',
    aliases: ['shed', 'carport', 'private garage', 'associated garage'],
    helperNote: 'Class 10a is for non-habitable buildings such as sheds, carports, and private garages.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Fence / mast / antenna / retaining wall / swimming pool',
    bcaClassification: '10b',
    aliases: ['fence', 'mast', 'antenna', 'retaining wall', 'retaining walls', 'swimming pool', 'pool'],
    helperNote: 'Class 10b is for structures such as fences, masts, retaining walls, and pools.',
    isDefault: true,
  },
  {
    partOfBuilding: 'Private bushfire shelter',
    bcaClassification: '10c',
    aliases: ['bushfire shelter'],
    helperNote: 'Class 10c is a private bushfire shelter associated with a Class 1a building.',
    isDefault: true,
  },
];
const defaultBuildingClassificationOptions: BuildingClassificationOption[] = nccBuildingClassificationHelperOptions.map(({ aliases: _aliases, helperNote: _helperNote, ...option }) => option);

const emptyBuildingClassification = (): BuildingClassificationRow => ({ partOfBuilding: '', bcaClassification: '' });
const certificateLocalTimeZone = 'Australia/Melbourne';
function formatDateInputInTimeZone(value = new Date(), timeZone = certificateLocalTimeZone) {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const partMap = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${partMap.year}-${partMap.month}-${partMap.day}`;
}
const getTodayDateInput = () => formatDateInputInTimeZone();
const isDrainageMatter = (matterType: string) => /drainage/i.test(String(matterType || ''));
const getDefaultDrawingPrefix = (matterType: string) => (isDrainageMatter(matterType) ? 'C' : 'S');
const getDefaultRevisionSequenceInput = (matterType: string) => (isDrainageMatter(matterType) ? civilRevisionSequenceDefault : structuralRevisionSequenceDefault);
const getCertificateFileKind = (matterType: string, purpose = 'design') => `${isDrainageMatter(matterType) ? 'C' : 'S'} ${purpose === 'inspection' ? 'Inspection ' : ''}Cert`;
const getCertificateSharePointTargetLabel = (matterType: string) => (
  isDrainageMatter(matterType)
    ? '2-Civil Documents / 4-Construction Packages'
    : '3-Structural Documents / 5-Construction Packages'
);
const getDefaultDocumentType = (section: string, matterType = STRUCTURAL_MATTER) => {
  if (section === 'Drawing') {
    return isDrainageMatter(matterType) ? 'Drainage drawings' : 'Structural drawings';
  }
  if (section === 'Computation') {
    return isDrainageMatter(matterType) ? 'Drainage computations' : 'Structural computations';
  }
  return '';
};
const defaultDocumentTypeValues = new Set([
  'structural drawings',
  'structural computations',
  'civil drawings',
  'civil computations',
  'drainage drawings',
  'drainage computations',
]);
function isDefaultDocumentTypeValue(value: string) {
  return defaultDocumentTypeValues.has(value.trim().toLowerCase());
}
function retargetDefaultDocumentTypes(documents: DocumentRow[], matterType: string, preparedBy = fallbackPreparedBy) {
  const nextDocuments = documents.length ? documents : defaultDocumentRows(matterType, preparedBy);
  return nextDocuments.map((row) => {
    const defaultDocumentType = getDefaultDocumentType(row.section, matterType);
    if (!defaultDocumentType) return row;
    if (row.documentType.trim() && !isDefaultDocumentTypeValue(row.documentType)) {
      return row;
    }
    return {
      ...row,
      documentType: defaultDocumentType,
      preparedBy: row.preparedBy || preparedBy,
    };
  });
}
function replaceCertificateKindSuffix(certificateNumber: string, matterType: string, purpose = 'design') {
  const normalized = certificateNumber.trim();
  if (!normalized) return normalized;
  const nextKind = getCertificateFileKind(matterType, purpose);
  if (/\b[SC]\s+(?:Inspection\s+)?Cert\b/i.test(normalized)) {
    return normalized.replace(/\b[SC]\s+(?:Inspection\s+)?Cert\b/i, nextKind);
  }
  return normalized;
}
const emptyDocument = (preparedBy = ''): DocumentRow => ({ section: '', documentNo: '', documentDate: getTodayDateInput(), documentType: '', numberOfPages: '', preparedBy });
const createPresetDocumentRow = (section: string, matterType = STRUCTURAL_MATTER, preparedBy = fallbackPreparedBy): DocumentRow => ({
  section,
  documentNo: '',
  documentDate: getTodayDateInput(),
  documentType: getDefaultDocumentType(section, matterType),
  numberOfPages: '',
  preparedBy,
});
const defaultDocumentRows = (matterType = STRUCTURAL_MATTER, preparedBy = fallbackPreparedBy): DocumentRow[] => [
  createPresetDocumentRow('Drawing', matterType, preparedBy),
  createPresetDocumentRow('Computation', matterType, preparedBy),
];
const emptyPerformanceRequirement = (): PerformanceRequirementRow => ({ relevantPerformanceRequirement: '', details: '' });
const emptyNccReference = (): NccReferenceRow => ({ version: '' });
const emptyProvision = (): ProvisionRow => ({ sectionProvision: '' });

function hasProvisionValue(rows: ProvisionRow[], targetValue: string) {
  const normalizedTarget = normalizeLookupLabel(targetValue);
  return rows.some((row) => normalizeLookupLabel(row.sectionProvision) === normalizedTarget);
}

function isAustralianStandardProvision(value: string, options: string[]) {
  const normalized = normalizeLookupLabel(value);
  return options.some((option) => normalizeLookupLabel(option) === normalized)
    || /^as(?:\s*\/\s*nzs)?\s*\d/i.test(normalized);
}

function ensureProvisionValues(rows: ProvisionRow[], requiredValues: string[]) {
  const nextRows = [...rows];
  requiredValues.forEach((requiredValue) => {
    const normalizedValue = String(requiredValue || '').replace(/\s+/g, ' ').trim();
    if (!normalizedValue || hasProvisionValue(nextRows, normalizedValue)) return;
    const blankIndex = nextRows.findIndex((row) => !row.sectionProvision.trim());
    if (blankIndex >= 0) {
      nextRows[blankIndex] = { sectionProvision: normalizedValue };
    } else {
      nextRows.push({ sectionProvision: normalizedValue });
    }
  });
  return nextRows.length ? nextRows : [emptyProvision()];
}

function replaceProvisionValues(requiredValues: string[]) {
  const normalizedValues = dedupeDropdownOptions(
    requiredValues.map((value) => String(value || '').replace(/\s+/g, ' ').trim()).filter(Boolean)
  );
  return normalizedValues.length
    ? normalizedValues.map((sectionProvision) => ({ sectionProvision }))
    : [emptyProvision()];
}

function removeProvisionValue(rows: ProvisionRow[], targetValue: string) {
  const normalizedTarget = normalizeLookupLabel(targetValue);
  const nextRows = rows.filter((row) => normalizeLookupLabel(row.sectionProvision) !== normalizedTarget);
  return nextRows.length ? nextRows : [emptyProvision()];
}

function hasRetainingWallBuildingClassification(rows: BuildingClassificationRow[]) {
  return rows.some((row) => /retaining wall/i.test(String(row.partOfBuilding || '')));
}

function defaultProvisionRows() {
  return [emptyProvision()];
}

const emptyForm = (): CertificateEditorForm => {
  const today = getTodayDateInput();
  return {
    certificateId: '',
    certificateNumber: '',
    projectId: '',
    projectNumber: '',
    projectFolderName: '',
    title: '',
    certificatePurpose: 'design',
    inspectionWorkClass: 'domestic',
    inspectionDate: '',
    buildingWorkInspected: '',
    inspectionFindings: '',
    inspectionLimitations: '',
    effectiveHeight: '',
    declarationInspectedPersonally: true,
    declarationInspectedWorkComplies: true,
    certificateType: 'Certificate of Compliance',
    issueDate: today,
    status: 'Draft',
    notes: '',
    versionTag: today.replace(/-/g, ''),
    issuedToEntity: '',
    buildingSurveyor: '',
    buildingSurveyorEmail: '',
    buildingSurveyorPostalAddress: '',
    buildingSurveyorPostcode: '',
    projectAddress: '',
    projectSuburb: '',
    projectPostcode: '',
    projectCouncil: '',
    natureOfBuildingWorkType: 'New Building',
    natureOfBuildingWorkDescription: '',
    storeysContained: '',
    riseInStoreys: '',
    bcaVersion: '',
    typeOfConstruction: '',
    matterType: STRUCTURAL_MATTER,
    engineerName: '',
    engineerEmail: '',
    engineerAddress: '',
    engineerRegistrationCategoryClass: '',
    engineerRegistrationNumber: '',
    engineerIssueDate: today,
    includePerformanceSolution: false,
    declarationPreparedDesign: true,
    declarationDesignComplies: true,
    declarationPerformanceSolutionComplies: false,
    declarationCompetent: true,
    buildingClassifications: [emptyBuildingClassification()],
    documents: defaultDocumentRows(STRUCTURAL_MATTER),
    performanceRequirements: [emptyPerformanceRequirement()],
    nccReferences: [emptyNccReference()],
    provisions: defaultProvisionRows(),
  };
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asArrayOfRecords(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asRecord(item)).filter((item): item is Record<string, unknown> => Boolean(item));
}

function asText(value: unknown) {
  return value === null || value === undefined ? '' : String(value);
}

function asBool(value: unknown) {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase();
  return ['1', 'true', 'yes', 'y'].includes(normalized);
}

function normalizeSuburb(value: string) {
  return value.trim().toLowerCase();
}

function normalizePostCode(value: unknown) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(Math.trunc(value)) : '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? String(Math.trunc(numeric)) : trimmed;
}

function dedupeDropdownOptions(values: Array<string | null | undefined>) {
  const seen = new Set<string>();
  const options: string[] = [];
  values.forEach((value) => {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) return;
    seen.add(key);
    options.push(normalized);
  });
  return options;
}

function normalizeLookupLabel(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function resolveCertificateLookupCandidate(options: CertificateOption[], lookupValue: string) {
  const normalizedLookup = normalizeLookupLabel(lookupValue);
  if (!normalizedLookup) {
    return { match: null as CertificateOption | null, ambiguous: false };
  }

  const uniqueById = (matches: CertificateOption[]) => {
    const deduped = matches.filter((match, index, list) =>
      list.findIndex((candidate) =>
        String(candidate.CertificateId || candidate.CertificateNumber || '').toLowerCase()
        === String(match.CertificateId || match.CertificateNumber || '').toLowerCase()
      ) === index
    );
    if (deduped.length === 1) {
      return { match: deduped[0], ambiguous: false };
    }
    return { match: null, ambiguous: deduped.length > 1 };
  };

  const exactMatches = options.filter((option) => {
    const normalizedNumber = normalizeLookupLabel(option.CertificateNumber);
    const normalizedId = normalizeLookupLabel(option.CertificateId);
    const normalizedProjectNumber = normalizeLookupLabel(option.ProjectNumber);
    return normalizedNumber === normalizedLookup || normalizedId === normalizedLookup || normalizedProjectNumber === normalizedLookup;
  });
  if (exactMatches.length) {
    return uniqueById(exactMatches);
  }

  const startsWithMatches = options.filter((option) =>
    normalizeLookupLabel(option.CertificateNumber).startsWith(normalizedLookup)
    || normalizeLookupLabel(option.ProjectNumber).startsWith(normalizedLookup)
  );
  if (startsWithMatches.length) {
    return uniqueById(startsWithMatches);
  }

  const containsMatches = options.filter((option) =>
    normalizeLookupLabel(option.CertificateNumber).includes(normalizedLookup)
    || normalizeLookupLabel(option.ProjectNumber).includes(normalizedLookup)
  );
  if (containsMatches.length) {
    return uniqueById(containsMatches);
  }

  return { match: null, ambiguous: false };
}

function mergeCertificateOptions(existing: CertificateOption[], incoming: CertificateOption[]) {
  const merged = [...existing];
  incoming.forEach((option) => {
    const optionKey = String(option.CertificateId || option.CertificateNumber || "").trim().toLowerCase();
    if (!optionKey) return;
    const existingIndex = merged.findIndex((candidate) =>
      String(candidate.CertificateId || candidate.CertificateNumber || "").trim().toLowerCase() === optionKey
    );
    if (existingIndex >= 0) {
      merged[existingIndex] = option;
      return;
    }
    merged.push(option);
  });
  return merged;
}

type CertificateDiscipline = 'structural' | 'civil' | 'unknown';

function resolveCertificateOptionDiscipline(option: CertificateOption): CertificateDiscipline {
  const label = `${option.CertificateNumber || ''} ${option.CertificateType || ''}`;
  if (/\b(?:c|civil)\s*(?:inspection\s+)?cert(?:ificate)?\b|\bcivil\b|drainage/i.test(label)) {
    return 'civil';
  }
  if (/\bs\s*(?:inspection\s+)?cert(?:ificate)?\b|structural/i.test(label)) {
    return 'structural';
  }
  return 'unknown';
}

function findLatestCertificateTemplate(
  options: CertificateOption[],
  projectNumber: string,
  matterType: string,
  purpose: string,
): CertificateOption | null {
  const normalizedProjectNumber = normalizeLookupLabel(projectNumber);
  if (!normalizedProjectNumber) return null;

  const targetDiscipline: Exclude<CertificateDiscipline, 'unknown'> = isDrainageMatter(matterType) ? 'civil' : 'structural';
  const matches = options
    .filter((option) => normalizeLookupLabel(option.ProjectNumber) === normalizedProjectNumber)
    .filter((option) => (/inspection/i.test(option.CertificateNumber || '') || option.CertificateType === 'Certificate of Compliance for Building Work') === (purpose === 'inspection'))
    .sort((left, right) => {
      const leftDate = Date.parse(String(left.IssueDate || ''));
      const rightDate = Date.parse(String(right.IssueDate || ''));
      if (Number.isFinite(leftDate) && Number.isFinite(rightDate) && leftDate !== rightDate) {
        return rightDate - leftDate;
      }
      return Number(right.CertificateId || 0) - Number(left.CertificateId || 0);
    });
  const disciplineMatch = matches.find((option) => resolveCertificateOptionDiscipline(option) === targetDiscipline);
  return disciplineMatch || matches.find((option) => resolveCertificateOptionDiscipline(option) === 'unknown') || null;
}

function dedupeBuildingClassificationOptions(options: BuildingClassificationOption[]) {
  const seen = new Set<string>();
  return options.filter((option) => {
    const partOfBuilding = String(option.partOfBuilding || '').replace(/\s+/g, ' ').trim();
    const bcaClassification = String(option.bcaClassification || '').replace(/\s+/g, ' ').trim();
    const key = `${partOfBuilding.toLowerCase()}|${bcaClassification.toLowerCase()}`;
    if (!partOfBuilding || !bcaClassification || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildBuildingClassificationOptionLabel(option: Pick<BuildingClassificationOption, 'partOfBuilding' | 'bcaClassification'>) {
  const partOfBuilding = String(option.partOfBuilding || '').replace(/\s+/g, ' ').trim();
  const bcaClassification = String(option.bcaClassification || '').replace(/\s+/g, ' ').trim();
  return partOfBuilding && bcaClassification ? `${partOfBuilding} (${bcaClassification})` : partOfBuilding || bcaClassification;
}

function findBuildingClassificationOption(options: Array<Pick<BuildingClassificationOption, 'partOfBuilding' | 'bcaClassification'>>, partOfBuilding: string) {
  const normalized = normalizeLookupLabel(partOfBuilding);
  if (!normalized) return null;
  return options.find((option) => normalizeLookupLabel(option.partOfBuilding) === normalized) || null;
}

function normalizeBuildingClassificationLookup(value: unknown) {
  return normalizeLookupLabel(value).replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function scoreBuildingClassificationHelperOption(option: BuildingClassificationHelperOption, lookupValue: string) {
  const normalizedLookup = normalizeBuildingClassificationLookup(lookupValue);
  if (!normalizedLookup) return 0;

  const lookupCandidates = [option.partOfBuilding, ...(option.aliases || [])]
    .map((candidate) => normalizeBuildingClassificationLookup(candidate))
    .filter(Boolean);

  let bestScore = 0;
  lookupCandidates.forEach((candidate) => {
    if (candidate === normalizedLookup) {
      bestScore = Math.max(bestScore, 1000 + candidate.length);
      return;
    }
    if (` ${normalizedLookup} `.includes(` ${candidate} `)) {
      bestScore = Math.max(bestScore, 100 + candidate.length);
    }
  });

  return bestScore;
}

function findBuildingClassificationHelperOption(lookupValue: string) {
  let bestMatch: BuildingClassificationHelperOption | null = null;
  let bestScore = 0;

  nccBuildingClassificationHelperOptions.forEach((option) => {
    const score = scoreBuildingClassificationHelperOption(option, lookupValue);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = option;
    }
  });

  return bestMatch;
}

function buildPractitionerOptionLabel(option: Pick<CertificatePractitionerOption, 'name' | 'registrationNumber'>) {
  const name = String(option.name || '').trim();
  const registrationNumber = String(option.registrationNumber || '').trim();
  if (!name) return '';
  return registrationNumber ? `${name} (${registrationNumber})` : name;
}

function scorePractitionerOption(option: CertificatePractitionerOption, normalizedLookup: string) {
  const normalizedName = option.name.trim().toLowerCase();
  const normalizedEmail = String(option.email || '').trim().toLowerCase();
  return (normalizedName === normalizedLookup ? 10 : 0)
    + (normalizedEmail === normalizedLookup ? 9 : 0)
    + (option.address ? 3 : 0)
    + (option.registrationCategoryClass ? 3 : 0)
    + (option.registrationNumber ? 3 : 0)
    + (option.company ? 1 : 0)
    + (option.email ? 1 : 0)
    + (option.sourceTable === 'dbo.CertificatePractitionerList' ? 1 : 0);
}

function findBestPractitionerOption(options: CertificatePractitionerOption[], lookupValue: string) {
  const normalizedLookup = String(lookupValue || '').trim().toLowerCase();
  if (!normalizedLookup) return null;

  let bestMatch: CertificatePractitionerOption | null = null;
  let bestScore = -1;

  options.forEach((option) => {
    const normalizedName = option.name.trim().toLowerCase();
    const normalizedEmail = String(option.email || '').trim().toLowerCase();
    if (normalizedName !== normalizedLookup && normalizedEmail !== normalizedLookup) return;
    const score = scorePractitionerOption(option, normalizedLookup);
    if (score > bestScore) {
      bestMatch = option;
      bestScore = score;
    }
  });

  return bestMatch;
}

function scoreBuildingSurveyorOption(option: CertificateBuildingSurveyorOption, normalizedLookup: string) {
  const normalizedName = option.name.trim().toLowerCase();
  const normalizedEmail = String(option.email || '').trim().toLowerCase();
  return (normalizedName === normalizedLookup ? 10 : 0)
    + (normalizedEmail === normalizedLookup ? 9 : 0)
    + (option.postalAddress ? 2 : 0)
    + (option.postcode ? 2 : 0)
    + (option.email ? 1 : 0);
}

function findBestBuildingSurveyorOption(options: CertificateBuildingSurveyorOption[], lookupValue: string) {
  const normalizedLookup = String(lookupValue || '').trim().toLowerCase();
  if (!normalizedLookup) return null;

  let bestMatch: CertificateBuildingSurveyorOption | null = null;
  let bestScore = -1;

  options.forEach((option) => {
    const normalizedName = option.name.trim().toLowerCase();
    const normalizedEmail = String(option.email || '').trim().toLowerCase();
    if (normalizedName !== normalizedLookup && normalizedEmail !== normalizedLookup) return;
    const score = scoreBuildingSurveyorOption(option, normalizedLookup);
    if (score > bestScore) {
      bestMatch = option;
      bestScore = score;
    }
  });

  return bestMatch;
}

function mergeBuildingSurveyorOption(
  options: CertificateBuildingSurveyorOption[],
  candidate: CertificateBuildingSurveyorOption | null
) {
  if (!candidate?.name?.trim()) return options;
  const normalizedCandidate = {
    name: candidate.name.trim(),
    email: String(candidate.email || '').trim().toLowerCase() || null,
    postalAddress: String(candidate.postalAddress || '').trim() || null,
    postcode: String(candidate.postcode || '').trim() || null,
    sourceTable: candidate.sourceTable || 'dbo.CertificateBuildingSurveyorList',
  } as CertificateBuildingSurveyorOption;
  const dedupeKey = [
    normalizedCandidate.name.toLowerCase(),
    normalizedCandidate.email || '',
    normalizedCandidate.postalAddress || '',
    normalizedCandidate.postcode || '',
  ].join('|');
  if (options.some((option) => [
    option.name.trim().toLowerCase(),
    String(option.email || '').trim().toLowerCase(),
    String(option.postalAddress || '').trim(),
    String(option.postcode || '').trim(),
  ].join('|') === dedupeKey)) {
    return options;
  }
  return [...options, normalizedCandidate].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

function resolveEngineerCertificateVariant(...values: Array<string | null | undefined>) {
  for (const value of values) {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!normalized) continue;
    if (/\byl\b/.test(normalized) || (normalized.includes('yuan') && normalized.includes('lin'))) {
      return 'structural';
    }
    if (/\bwd\b/.test(normalized) || (normalized.includes('wilson') && normalized.includes('du'))) {
      return 'drainage';
    }
  }
  return 'generic';
}

function resolveCertificateHeroTitle(...values: Array<string | null | undefined>) {
  const variant = resolveEngineerCertificateVariant(...values);
  if (variant === 'structural') return 'STRUCTURAL CERTIFICATE';
  if (variant === 'drainage') return 'DRAINAGE CERTIFICATE';
  return 'CERTIFICATE';
}

function resolveBuildingClassificationValue(
  options: Array<Pick<BuildingClassificationOption, 'partOfBuilding' | 'bcaClassification'>>,
  partOfBuilding: string,
  fallback = ''
) {
  const option = findBuildingClassificationOption(options, partOfBuilding) || findBuildingClassificationPreset(partOfBuilding);
  return option?.bcaClassification || fallback;
}

function findBuildingClassificationPreset(partOfBuilding: string) {
  return findBuildingClassificationHelperOption(partOfBuilding)
    || findBuildingClassificationOption(defaultBuildingClassificationOptions, partOfBuilding);
}

function getStoredAustralianStandardOptions() {
  if (typeof window === 'undefined') return [] as string[];
  try {
    const raw = window.localStorage.getItem(australianStandardStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return dedupeDropdownOptions(parsed.map((value) => String(value || '')));
  } catch {
    return [];
  }
}

function normalizeDocumentPages(value: unknown) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const compact = raw.replace(/^\(+|\)+$/g, '').replace(/\bto\b/gi, '-').replace(/\s+/g, '');
  if (!compact) return '';
  const withoutPrefix = compact.replace(/^P/i, '');
  if (!withoutPrefix) return '';
  if (/^\d+$/.test(withoutPrefix)) {
    const finalPage = String(Number.parseInt(withoutPrefix, 10));
    if (!finalPage || finalPage === '0') return '';
    return finalPage === '1' ? 'P1' : `P1-${finalPage}`;
  }
  const rangeMatch = withoutPrefix.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const [, startPage, endPage] = rangeMatch;
    return startPage === endPage ? `P${startPage}` : `P${startPage}-${endPage}`;
  }
  return `P${withoutPrefix}`;
}

function parseFinalPageNumber(value: unknown) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = normalizeDocumentPages(raw);
  const rangeMatch = normalized.match(/^P(\d+)-(\d+)$/i);
  if (rangeMatch) {
    return rangeMatch[2];
  }
  const singleMatch = normalized.match(/^P(\d+)$/i);
  return singleMatch ? singleMatch[1] : '';
}

function parseRevisionSequence(value: string) {
  const tokens = String(value || '')
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  return tokens.filter((token) => {
    const key = token.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeDrawingDocNoSettings(settings: DrawingDocNoGeneratorSettings): DrawingDocNoGeneratorSettings {
  const prefix = String(settings.prefix || '').trim().toUpperCase().slice(0, 2) || 'S';
  const rawTotalPages = String(settings.totalPages || '').trim();
  const parsedTotalPages = Number.parseInt(rawTotalPages, 10);
  const totalPages = Number.isFinite(parsedTotalPages) && parsedTotalPages > 0 ? String(Math.min(parsedTotalPages, 200)) : '1';
  const revisionTokens = parseRevisionSequence(settings.revisionSequenceInput);
  return {
    prefix,
    totalPages,
    revisionSequenceInput: revisionTokens.length ? revisionTokens.join(',') : 'A',
  };
}

function buildDrawingSheetCodes(prefix: string, totalPages: string) {
  const pageCount = Number.parseInt(String(totalPages || '0'), 10);
  if (!Number.isFinite(pageCount) || pageCount <= 0) return [] as string[];
  return Array.from({ length: pageCount }, (_, index) => `${prefix}${String(index + 1).padStart(2, '0')}`);
}

function buildDrawingRevisionSelections(
  sheetCodes: string[],
  revisionTokens: string[],
  existingSelections: Record<string, string> = {}
) {
  const fallbackRevision = revisionTokens[0] || '';
  return sheetCodes.reduce<Record<string, string>>((accumulator, sheetCode) => {
    const existingRevision = existingSelections[sheetCode];
    accumulator[sheetCode] = existingRevision && revisionTokens.includes(existingRevision) ? existingRevision : fallbackRevision;
    return accumulator;
  }, {});
}

function buildGeneratedDrawingDocNo(projectNumber: string, sheetCodes: string[], revisionSelections: Record<string, string>) {
  const entries = sheetCodes
    .map((sheetCode) => {
      const revision = String(revisionSelections[sheetCode] || '').trim();
      return revision ? `${sheetCode}[${revision}]` : '';
    })
    .filter(Boolean);
  if (!entries.length) return '';
  const prefix = projectNumber.trim() ? `${projectNumber.trim()} - ` : '';
  return `${prefix}${entries.join(', ')}`;
}

function isDrawingDocumentTypeTarget(documentType: string, prefix: string) {
  const normalizedType = String(documentType || '').trim().toLowerCase();
  if (!normalizedType) return false;
  if (prefix.toUpperCase().startsWith('C')) {
    return normalizedType.includes('civil') || normalizedType.includes('drainage');
  }
  return normalizedType.includes('structural');
}

function buildLocalDate(year: number, month: number, day: number) {
  const parsed = new Date(year, month - 1, day);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function parseDateValue(value?: unknown) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const auDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (auDate) {
    const [, day, month, year] = auDate;
    return buildLocalDate(Number(year), Number(month), Number(day));
  }
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return buildLocalDate(Number(year), Number(month), Number(day));
  }
  const compactDate = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactDate) {
    const [, year, month, day] = compactDate;
    return buildLocalDate(Number(year), Number(month), Number(day));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateOnly(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toDateInput(value: unknown, fallback = '') {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  const parsed = parseDateValue(raw);
  return parsed ? formatDateOnly(parsed) : raw.slice(0, 10);
}

function normalizeDateInputValue(value: unknown) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = parseDateValue(raw);
  return parsed ? formatDateOnly(parsed) : '';
}

function formatDateLabel(value?: string | null) {
  if (!value) return 'Not yet';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-AU');
}

function formatCompactDate(value?: unknown) {
  return (normalizeDateInputValue(value) || getTodayDateInput()).replace(/-/g, '');
}

function normalizeDocumentNameSegment(value: string) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();
}

function hasCompactDateToken(value: string) {
  return /\b20\d{6}\b/.test(String(value || ''));
}

function buildCertificateFileSuffix(issueDate?: unknown, revisionLabelValue?: unknown) {
  const datePart = formatCompactDate(issueDate);
  const revisionLabel = normalizeDocumentNameSegment(String(revisionLabelValue || ''));
  return revisionLabel && !hasCompactDateToken(revisionLabel)
    ? `${revisionLabel} ${datePart}`
    : datePart;
}

function isDrawingDocumentRow(row: DocumentRow) {
  return /drawing/i.test(`${row.section} ${row.documentType}`);
}

function normalizeProjectNameSuburbSegment(value: string, suburb: string) {
  const normalizedValue = normalizeDocumentNameSegment(value);
  const normalizedSuburb = normalizeDocumentNameSegment(suburb);
  const formattedSuburb = formatSuburbLabel(normalizedSuburb);
  if (!normalizedValue || !normalizedSuburb || !formattedSuburb) return normalizedValue;
  const escapedSuburb = normalizedSuburb.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return normalizedValue.replace(new RegExp(`\\b${escapedSuburb}\\b`, 'gi'), formattedSuburb);
}

function stripTrailingSuburb(address: string, suburb: string) {
  const normalizedAddress = normalizeDocumentNameSegment(address);
  const normalizedSuburb = normalizeDocumentNameSegment(suburb);
  if (!normalizedAddress || !normalizedSuburb) return normalizedAddress;
  const escapedSuburb = normalizedSuburb.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return normalizedAddress.replace(new RegExp(`(?:,\\s*|\\s+)${escapedSuburb}$`, 'i'), '').trim();
}

function isComputationDocumentRow(row: DocumentRow) {
  return row.section.trim().toLowerCase() === 'computation' || /computation/i.test(row.documentType);
}

function resolveLatestDocumentDate(rows: DocumentRow[]) {
  return rows.reduce((latest, row) => {
    const date = normalizeDateInputValue(row.documentDate);
    return date > latest ? date : latest;
  }, '');
}

function resolveCertificateDateFromForm(form: Pick<CertificateEditorForm, 'certificatePurpose' | 'documents' | 'engineerIssueDate' | 'issueDate'>) {
  return (form.certificatePurpose === 'inspection' ? '' : resolveLatestDocumentDate(form.documents))
    || normalizeDateInputValue(form.engineerIssueDate)
    || normalizeDateInputValue(form.issueDate)
    || getTodayDateInput();
}

function resolveCertificateVersionTag(form: Pick<CertificateEditorForm, 'versionTag' | 'certificatePurpose' | 'documents' | 'engineerIssueDate' | 'issueDate'>) {
  return form.versionTag.trim() || formatCompactDate(resolveCertificateDateFromForm(form));
}

function shouldAutoReplaceVersionTag(value: string) {
  const normalized = value.trim();
  return !normalized || /^\d{8}$/.test(normalized);
}

function getDocumentNamePlaceholder(row: DocumentRow, matterType = STRUCTURAL_MATTER) {
  if (row.section.trim().toLowerCase() === 'drawing') {
    return 'Click Generate Doc No.';
  }
  if (isComputationDocumentRow(row)) {
    return `Project no. - address suburb - ${isDrainageMatter(matterType) ? 'C Comps' : 'S Comps'} 20260408`;
  }
  return 'Document name';
}

function buildAutoComputationDocumentName(
  form: Pick<CertificateEditorForm, 'projectNumber' | 'projectAddress' | 'projectSuburb' | 'matterType'>,
  documentDate?: string | null
) {
  const projectNumber = normalizeDocumentNameSegment(form.projectNumber);
  if (!projectNumber) return '';
  const trimmedAddress = stripTrailingSuburb(form.projectAddress, form.projectSuburb);
  const projectAddress = normalizeDocumentNameSegment(trimmedAddress);
  const projectSuburb = formatSuburbLabel(form.projectSuburb);
  const documentTag = isDrainageMatter(form.matterType) ? 'C Comps' : 'S Comps';
  const dateLabel = formatCompactDate(documentDate);
  const addressBlock = [projectAddress, projectSuburb].filter(Boolean).join(' ');
  const trailingBlock = [documentTag, dateLabel].filter(Boolean).join(' ');
  return [
    projectNumber,
    addressBlock,
    trailingBlock,
  ].filter(Boolean).join(' - ');
}

function serializeCertificateEditorForm(form: CertificateEditorForm) {
  return JSON.stringify(form);
}

async function fileToBase64(file: File): Promise<string> {
  return blobToBase64(file);
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, contentType = 'application/pdf') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteNumbers[index] = byteCharacters.charCodeAt(index);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

function openPdfBlob(blob: Blob, previewWindow?: Window | null) {
  const url = URL.createObjectURL(blob);
  const targetWindow = previewWindow && !previewWindow.closed ? previewWindow : window.open('', '_blank');
  if (targetWindow) {
    try {
      targetWindow.location.replace(url);
    } catch {
      window.open(url, '_blank');
    }
  } else {
    window.open(url, '_blank');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 300_000);
}

function openPendingPdfPreviewWindow() {
  if (typeof window === 'undefined') return null;
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) return null;
  try {
    previewWindow.document.title = 'Preparing certificate preview...';
      previewWindow.document.body.innerHTML = `
      <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #0f172a;">
        <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px;">Preparing certificate preview...</div>
        <div style="font-size: 14px; color: #475569;">The PDF will open here as soon as rendering finishes.</div>
      </div>
    `;
  } catch {
    return previewWindow;
  }
  return previewWindow;
}

function buildDraftPdfFileName(form: CertificateEditorForm) {
  const rawPrefix = form.projectNumber.trim() || form.certificateNumber.trim() || 'Certificate';
  const basePrefix = normalizeProjectNameSuburbSegment(rawPrefix, form.projectSuburb);
  const projectAddress = normalizeDocumentNameSegment(stripTrailingSuburb(form.projectAddress, form.projectSuburb));
  const projectSuburb = formatSuburbLabel(form.projectSuburb);
  const locationBlock = [projectAddress, projectSuburb].filter(Boolean).join(' ');
  const prefix = locationBlock && !basePrefix.toLowerCase().includes(locationBlock.toLowerCase())
    ? `${basePrefix} - ${locationBlock}`
    : basePrefix;
  const tail = buildCertificateFileSuffix(resolveCertificateDateFromForm(form), form.versionTag);
  return `${prefix} ${getCertificateFileKind(form.matterType, form.certificatePurpose)} ${tail}.pdf`.replace(/\s+/g, ' ').trim();
}

function buildNatureOfBuildingWorkText(type: string, description: string) {
  const normalizedType = String(type || '').trim();
  const normalizedDescription = String(description || '').trim();
  if (normalizedType && normalizedDescription) {
    return `${normalizedType}: ${normalizedDescription}`;
  }
  return normalizedType || normalizedDescription;
}

function parseNatureOfBuildingWork(snapshot: Record<string, unknown>) {
  const selectedType = asText(snapshot.NatureOfBuildingWorkType || snapshot.natureOfBuildingWorkType).trim();
  const selectedDescription = asText(snapshot.NatureOfBuildingWorkDescription || snapshot.natureOfBuildingWorkDescription).trim();
  if (selectedType || selectedDescription) {
    return {
      type: selectedType,
      description: selectedDescription,
    };
  }

  const combined = asText(snapshot.NatureOfProposedBuildingWork).trim();
  if (!combined) {
    return {
      type: 'New Building',
      description: '',
    };
  }

  const matchedType = natureOfBuildingWorkTypeOptions
    .filter(Boolean)
    .find((option) => combined.toLowerCase().startsWith(option.toLowerCase()));

  if (!matchedType) {
    return {
      type: '',
      description: combined,
    };
  }

  return {
    type: matchedType,
    description: combined.slice(matchedType.length).replace(/^:\s*/, '').trim(),
  };
}

function mapDraftToForm(draft: CertificateDraftResponse): CertificateEditorForm {
  const snapshot = asRecord(draft.snapshot) || {};
  const natureOfBuildingWork = parseNatureOfBuildingWork(snapshot);

  const buildingClassifications = asArrayOfRecords(snapshot.BuildingClassifications).map((row) => ({
    partOfBuilding: asText(row.PartOfBuilding || row.partOfBuilding),
    bcaClassification:
      asText(row.BcaClassification || row.bcaClassification)
      || findBuildingClassificationPreset(asText(row.PartOfBuilding || row.partOfBuilding))?.bcaClassification
      || '',
  }));

  const documents = asArrayOfRecords(snapshot.Documents).map((row) => ({
    section: asText(row.Section || row.section),
    documentNo: asText(row.DocumentNo || row.documentNo),
    documentDate: toDateInput(row.DocumentDate || row.documentDate),
    documentType: asText(row.DocumentType || row.documentType),
    numberOfPages: normalizeDocumentPages(row.NumberOfPages || row.numberOfPages),
    preparedBy: asText(row.PreparedBy || row.preparedBy) || fallbackPreparedBy,
  }));

  const performanceRequirements = asArrayOfRecords(snapshot.PerformanceRequirements).map((row) => ({
    relevantPerformanceRequirement: asText(row.RelevantPerformanceRequirement || row.relevantPerformanceRequirement),
    details: asText(row.Details || row.details),
  }));

  const nccReferences = asArrayOfRecords(snapshot.NccReferences).map((row) => ({
    version: asText(row.Version || row.version),
  }));

  const provisions = asArrayOfRecords(snapshot.Provisions).map((row) => ({
    sectionProvision: asText(row.SectionProvision || row.sectionProvision),
  }));

  const resolvedNccReferences = nccReferences.length
    ? nccReferences
    : (asText(snapshot.BcaVersion)
      ? [{ version: asText(snapshot.BcaVersion) }]
      : [emptyNccReference()]);
  const resolvedMatterType = normalizeMatterTypeLabel(snapshot.MatterType || draft.certificateType);
  const resolvedDocuments = documents.length ? documents : defaultDocumentRows(resolvedMatterType);
  const fallbackToday = getTodayDateInput();
  const latestDocumentDate = snapshot.CertificatePurpose === 'inspection' ? '' : resolveLatestDocumentDate(resolvedDocuments);
  const loadedIssueDate = normalizeDateInputValue(draft.issueDate || snapshot.IssueDate || snapshot.DateIssued) || fallbackToday;
  const certificateDate = latestDocumentDate || loadedIssueDate || fallbackToday;
  const engineerIssueDate = latestDocumentDate || normalizeDateInputValue(snapshot.EngineerIssueDate || draft.issueDate) || certificateDate;

  return {
    certificateId: draft.certificateId ? String(draft.certificateId) : '',
    certificateNumber: draft.certificateNumber || asText(snapshot.CertificateNumber),
    projectId: draft.projectId ? String(draft.projectId) : '',
    projectNumber: draft.projectNumber || asText(snapshot.ProjectNumber),
    projectFolderName: draft.projectFolderName || '',
    title: draft.title || asText(snapshot.ProjectName || snapshot.Title),
    certificatePurpose: snapshot.CertificatePurpose === 'inspection' ? 'inspection' : 'design',
    inspectionWorkClass: asText(snapshot.InspectionWorkClass) || 'domestic',
    inspectionDate: toDateInput(snapshot.InspectionDate),
    buildingWorkInspected: asText(snapshot.BuildingWorkInspected),
    inspectionFindings: asText(snapshot.InspectionFindings),
    inspectionLimitations: asText(snapshot.InspectionLimitations),
    effectiveHeight: asText(snapshot.EffectiveHeight),
    declarationInspectedPersonally: snapshot.DeclarationInspectedPersonally === undefined ? true : asBool(snapshot.DeclarationInspectedPersonally),
    declarationInspectedWorkComplies: snapshot.DeclarationInspectedWorkComplies === undefined ? true : asBool(snapshot.DeclarationInspectedWorkComplies),
    certificateType: draft.certificateType || asText(snapshot.CertificateType || snapshot.MatterType) || 'Certificate of Compliance',
    issueDate: certificateDate,
    status: draft.status || 'Draft',
    notes: draft.notes || '',
    versionTag: draft.versionTag || asText(snapshot.VersionTag) || formatCompactDate(certificateDate),
    issuedToEntity: asText(snapshot.IssuedToEntity),
    buildingSurveyor: asText(snapshot.BuildingSurveyor),
    buildingSurveyorEmail: asText(snapshot.BuildingSurveyorEmail),
    buildingSurveyorPostalAddress: asText(snapshot.BuildingSurveyorPostalAddress || snapshot.BuildingSurveyorPostal),
    buildingSurveyorPostcode: asText(snapshot.BuildingSurveyorPostcode),
    projectAddress: draft.projectAddress || asText(snapshot.ProjectAddress),
    projectSuburb: draft.projectSuburb || asText(snapshot.ProjectSuburb),
    projectPostcode: draft.projectPostcode || asText(snapshot.ProjectPostcode),
    projectCouncil: draft.projectCouncil || asText(snapshot.ProjectCouncil),
    natureOfBuildingWorkType: natureOfBuildingWork.type,
    natureOfBuildingWorkDescription: natureOfBuildingWork.description,
    storeysContained: asText(snapshot.StoreysContained),
    riseInStoreys: asText(snapshot.RiseInStoreys),
    typeOfConstruction: asText(snapshot.TypeOfConstruction),
    bcaVersion: resolvedNccReferences.find((row) => row.version.trim())?.version || asText(snapshot.BcaVersion),
    matterType: resolvedMatterType,
    engineerName: asText(snapshot.EngineerName || snapshot.PractitionerName),
    engineerEmail: asText(snapshot.EngineerEmail || snapshot.PractitionerEmail),
    engineerAddress: asText(snapshot.EngineerAddress || snapshot.PractitionerPostalAddress),
    engineerRegistrationCategoryClass: asText(snapshot.EngineerRegistrationCategoryClass),
    engineerRegistrationNumber: asText(snapshot.EngineerRegistrationNumber),
    engineerIssueDate,
    includePerformanceSolution: asBool(snapshot.IncludePerformanceSolution),
    declarationPreparedDesign: snapshot.DeclarationPreparedDesign === undefined ? true : asBool(snapshot.DeclarationPreparedDesign),
    declarationDesignComplies: snapshot.DeclarationDesignComplies === undefined ? true : asBool(snapshot.DeclarationDesignComplies),
    declarationPerformanceSolutionComplies: asBool(snapshot.DeclarationPerformanceSolutionComplies),
    declarationCompetent: snapshot.DeclarationCompetent === undefined ? true : asBool(snapshot.DeclarationCompetent),
    buildingClassifications: buildingClassifications.length ? buildingClassifications : [emptyBuildingClassification()],
    documents: resolvedDocuments,
    performanceRequirements: performanceRequirements.length ? performanceRequirements : [emptyPerformanceRequirement()],
    nccReferences: resolvedNccReferences,
    provisions: provisions.length ? provisions : defaultProvisionRows(),
  };
}

function buildSnapshotFromForm(form: CertificateEditorForm) {
  const certificateDate = resolveCertificateDateFromForm(form);
  const normalizedVersionTag = resolveCertificateVersionTag(form);
  const primaryNccProvision = form.nccReferences.find((row) => row.version.trim())?.version.trim() || form.bcaVersion.trim();
  const natureOfBuildingWork = buildNatureOfBuildingWorkText(form.natureOfBuildingWorkType, form.natureOfBuildingWorkDescription);
  return {
    CertificatePurpose: form.certificatePurpose,
    InspectionWorkClass: form.inspectionWorkClass,
    InspectionDate: form.inspectionDate,
    BuildingWorkInspected: form.buildingWorkInspected,
    InspectionFindings: form.inspectionFindings,
    InspectionLimitations: form.inspectionLimitations,
    EffectiveHeight: form.effectiveHeight,
    DeclarationInspectedPersonally: form.declarationInspectedPersonally,
    DeclarationInspectedWorkComplies: form.declarationInspectedWorkComplies,
    CertificateNumber: form.certificateNumber || undefined,
    CertificateType: form.certificateType || undefined,
    ProjectNumber: form.projectNumber || undefined,
    ProjectAddress: form.projectAddress || undefined,
    ProjectSuburb: form.projectSuburb || undefined,
    ProjectPostcode: form.projectPostcode || undefined,
    ProjectCouncil: form.projectCouncil || undefined,
    ProjectName: form.title || undefined,
    Title: form.title || undefined,
    VersionTag: normalizedVersionTag,
    IssueDate: certificateDate || undefined,
    DateIssued: certificateDate || undefined,
    IssuedToEntity: form.issuedToEntity || undefined,
    BuildingSurveyor: form.buildingSurveyor || undefined,
    BuildingSurveyorEmail: form.buildingSurveyorEmail || undefined,
    BuildingSurveyorPostalAddress: form.buildingSurveyorPostalAddress || undefined,
    BuildingSurveyorPostcode: form.buildingSurveyorPostcode || undefined,
    NatureOfBuildingWorkType: form.natureOfBuildingWorkType || undefined,
    NatureOfBuildingWorkDescription: form.natureOfBuildingWorkDescription || undefined,
    NatureOfProposedBuildingWork: natureOfBuildingWork || undefined,
    StoreysContained: form.storeysContained || undefined,
    RiseInStoreys: form.riseInStoreys || undefined,
    TypeOfConstruction: form.typeOfConstruction || undefined,
    BcaVersion: primaryNccProvision || undefined,
    MatterType: form.matterType || form.certificateType || undefined,
    IncludePerformanceSolution: form.includePerformanceSolution,
    DeclarationPreparedDesign: form.declarationPreparedDesign,
    DeclarationDesignComplies: form.declarationDesignComplies,
    DeclarationPerformanceSolutionComplies: form.declarationPerformanceSolutionComplies,
    DeclarationCompetent: form.declarationCompetent,
    EngineerName: form.engineerName || undefined,
    EngineerEmail: form.engineerEmail || undefined,
    EngineerAddress: form.engineerAddress || undefined,
    EngineerRegistrationCategoryClass: form.engineerRegistrationCategoryClass || undefined,
    EngineerRegistrationNumber: form.engineerRegistrationNumber || undefined,
    EngineerIssueDate: certificateDate || undefined,
    BuildingClassifications: form.buildingClassifications
      .filter((row) => row.partOfBuilding.trim() || row.bcaClassification.trim())
      .map((row) => ({ PartOfBuilding: row.partOfBuilding.trim(), BcaClassification: row.bcaClassification.trim() })),
    Documents: form.documents
      .filter((row) => row.section.trim() || row.documentNo.trim() || row.documentType.trim())
      .map((row) => ({
        Section: row.section.trim(),
        DocumentNo: row.documentNo.trim(),
        DocumentDate: row.documentDate || '',
        DocumentType: row.documentType.trim(),
        NumberOfPages: normalizeDocumentPages(row.numberOfPages),
        PreparedBy: row.preparedBy.trim(),
      })),
    PerformanceRequirements: form.performanceRequirements
      .filter((row) => row.relevantPerformanceRequirement.trim() || row.details.trim())
      .map((row) => ({
        RelevantPerformanceRequirement: row.relevantPerformanceRequirement.trim(),
        Details: row.details.trim(),
      })),
    NccReferences: form.nccReferences
      .filter((row) => row.version.trim())
      .map((row) => ({ Version: row.version.trim() })),
    Provisions: form.provisions
      .filter((row) => row.sectionProvision.trim())
      .map((row) => ({ SectionProvision: row.sectionProvision.trim() })),
  };
}

function updateListRow<T>(rows: T[], index: number, nextRow: T) {
  return rows.map((row, rowIndex) => (rowIndex === index ? nextRow : row));
}

function renderHistoryValue(record: any, keys: string[], fallback = 'Not set') {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== null && value !== undefined && value !== '') {
      return String(value);
    }
  }
  return fallback;
}

const CertificateWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPhone = useIsNarrowScreen(720);
  const signedInEngineerName = resolveCertificateEngineerNameForUser(user?.userDetails);
  const createNewCertificateForm = () => ({
    ...emptyForm(),
    engineerName: signedInEngineerName,
  });
  const [mode, setMode] = useState<WorkflowMode>('create');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [versions, setVersions] = useState<any[] | null>(null);
  const [projectQuery, setProjectQuery] = useState('');
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [projectLookupBusy, setProjectLookupBusy] = useState(false);
  const [projectLookupMessage, setProjectLookupMessage] = useState('');
  const [certificateLookup, setCertificateLookup] = useState('');
  const [certificateOptions, setCertificateOptions] = useState<CertificateOption[]>([]);
  const [form, setForm] = useState<CertificateEditorForm>(() => createNewCertificateForm());
  const [storedDraftPdf, setStoredDraftPdf] = useState<StoredDraftPdf | null>(null);
  const [sourceKind, setSourceKind] = useState<'current' | 'civil' | null>(null);
  const [useProposedCertificateStyle, setUseProposedCertificateStyle] = useState(true);
  const [confirmForm, setConfirmForm] = useState({ certificateId: '', pdfFile: null as File | null });
  const [certificateApproval, setCertificateApproval] = useState<DocumentApprovalState | null>(null);
  const [certificateApprovalCode, setCertificateApprovalCode] = useState('');
  const [historyLookupId, setHistoryLookupId] = useState('');
  const [suburbOptions, setSuburbOptions] = useState<SuburbLookupRow[]>([]);
  const [suburbLookupMessage, setSuburbLookupMessage] = useState('');
  const [australianStandardOptions, setAustralianStandardOptions] = useState<string[]>(() => getStoredAustralianStandardOptions());
  const [newAustralianStandard, setNewAustralianStandard] = useState('');
  const [australianStandardMessage, setAustralianStandardMessage] = useState('');
  const [buildingClassificationOptions, setBuildingClassificationOptions] = useState<BuildingClassificationOption[]>([]);
  const [buildingClassificationOptionsBusy, setBuildingClassificationOptionsBusy] = useState(false);
  const [buildingClassificationMessage, setBuildingClassificationMessage] = useState('');
  const [newBuildingPartOption, setNewBuildingPartOption] = useState('');
  const [newBuildingClassOption, setNewBuildingClassOption] = useState('');
  const [practitionerOptions, setPractitionerOptions] = useState<CertificatePractitionerOption[]>([]);
  const [practitionerOptionsBusy, setPractitionerOptionsBusy] = useState(false);
  const [practitionerOptionsMessage, setPractitionerOptionsMessage] = useState('');
  const [buildingSurveyorOptions, setBuildingSurveyorOptions] = useState<CertificateBuildingSurveyorOption[]>([]);
  const [buildingSurveyorOptionsBusy, setBuildingSurveyorOptionsBusy] = useState(false);
  const [buildingSurveyorOptionsMessage, setBuildingSurveyorOptionsMessage] = useState('');
  const [drawingDocNoGeneratorOpen, setDrawingDocNoGeneratorOpen] = useState(false);
  const [drawingDocNoSettingsDraft, setDrawingDocNoSettingsDraft] = useState<DrawingDocNoGeneratorSettings>(() => normalizeDrawingDocNoSettings({
    prefix: getDefaultDrawingPrefix(STRUCTURAL_MATTER),
    totalPages: '1',
    revisionSequenceInput: getDefaultRevisionSequenceInput(STRUCTURAL_MATTER),
  }));
  const [drawingDocNoSettingsApplied, setDrawingDocNoSettingsApplied] = useState<DrawingDocNoGeneratorSettings>(() => normalizeDrawingDocNoSettings({
    prefix: getDefaultDrawingPrefix(STRUCTURAL_MATTER),
    totalPages: '1',
    revisionSequenceInput: getDefaultRevisionSequenceInput(STRUCTURAL_MATTER),
  }));
  const [drawingDocNoSelections, setDrawingDocNoSelections] = useState<Record<string, string>>({});
  const [drawingDocNoMessage, setDrawingDocNoMessage] = useState('');
  const [baselineFormJson, setBaselineFormJson] = useState(() => serializeCertificateEditorForm(createNewCertificateForm()));
  const autoPreparedByRef = useRef(fallbackPreparedBy);
  const autoComputationDocumentNamesRef = useRef<string[]>([]);

  const availableNccProvisionOptions = useMemo(
    () => dedupeDropdownOptions([...nccProvisionOptions, form.bcaVersion, ...form.nccReferences.map((row) => row.version)]),
    [form.bcaVersion, form.nccReferences]
  );

  const availableBuildingClassificationOptions = useMemo(
    () => dedupeBuildingClassificationOptions([
      ...defaultBuildingClassificationOptions,
      ...buildingClassificationOptions,
      ...form.buildingClassifications
        .filter((row) => row.partOfBuilding.trim() || row.bcaClassification.trim())
        .map((row) => ({ partOfBuilding: row.partOfBuilding, bcaClassification: row.bcaClassification })),
    ]),
    [buildingClassificationOptions, form.buildingClassifications]
  );

  const availableBuildingClassOptions = useMemo(
    () => dedupeDropdownOptions(availableBuildingClassificationOptions.map((option) => option.bcaClassification)),
    [availableBuildingClassificationOptions]
  );

  const buildingClassificationHelperMatch = useMemo(
    () => findBuildingClassificationHelperOption(newBuildingPartOption),
    [newBuildingPartOption]
  );

  const availableAustralianStandardOptions = useMemo(
    () => {
      const savedOptions = [...defaultAustralianStandardOptions, ...australianStandardOptions];
      return dedupeDropdownOptions([
        ...savedOptions,
        ...form.provisions
          .filter((row) => form.certificatePurpose !== 'inspection' || isAustralianStandardProvision(row.sectionProvision, savedOptions))
          .map((row) => row.sectionProvision),
      ]);
    },
    [australianStandardOptions, form.provisions, form.certificatePurpose]
  );

  const availablePractitionerSelectOptions = useMemo(
    () => {
      const seen = new Set<string>();
      return practitionerOptions
        .map((option) => {
          const value = String(option.name || '').trim();
          if (!value) return null;
          const normalizedValue = value.toLowerCase();
          if (seen.has(normalizedValue)) return null;
          seen.add(normalizedValue);
          return {
            value,
            label: buildPractitionerOptionLabel(option),
          };
        })
        .filter((option): option is { value: string; label: string } => Boolean(option));
    },
    [practitionerOptions]
  );

  const availableBuildingSurveyorNameOptions = useMemo(
    () => dedupeDropdownOptions(buildingSurveyorOptions.map((option) => option.name)),
    [buildingSurveyorOptions]
  );

  const availableBuildingSurveyorEmailOptions = useMemo(
    () => dedupeDropdownOptions(buildingSurveyorOptions.map((option) => option.email || '')),
    [buildingSurveyorOptions]
  );

  const certificateHeroTitle = useMemo(
    () => form.certificatePurpose === 'inspection' ? 'INSPECTION CERTIFICATE' : resolveCertificateHeroTitle(form.engineerName, form.engineerEmail),
    [form.certificatePurpose, form.engineerEmail, form.engineerName]
  );

  const preferredPreparedBy = useMemo(() => {
    const practitioner = findBestPractitionerOption(practitionerOptions, form.engineerName || form.engineerEmail);
    return practitioner?.company?.trim() || fallbackPreparedBy;
  }, [practitionerOptions, form.engineerEmail, form.engineerName]);

  const availablePreparedByOptions = useMemo(
    () => dedupeDropdownOptions([fallbackPreparedBy, ...preparedByOptions, preferredPreparedBy, ...form.documents.map((row) => row.preparedBy)]),
    [form.documents, preferredPreparedBy]
  );

  const filteredCertificateOptions = useMemo(() => {
    const query = certificateLookup.trim().toLowerCase();
    if (!query) {
      return certificateOptions.slice(0, 8);
    }
    return certificateOptions
      .filter((certificate) =>
        String(certificate.CertificateNumber || '').toLowerCase().includes(query)
        || String(certificate.ProjectNumber || '').toLowerCase().includes(query)
        || String(certificate.ProjectLocation || '').toLowerCase().includes(query)
        || String(certificate.CertificateType || '').toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [certificateLookup, certificateOptions]);

  const drawingDocNoRevisionTokens = useMemo(
    () => parseRevisionSequence(drawingDocNoSettingsApplied.revisionSequenceInput),
    [drawingDocNoSettingsApplied.revisionSequenceInput]
  );

  const drawingDocNoSheetCodes = useMemo(
    () => buildDrawingSheetCodes(drawingDocNoSettingsApplied.prefix, drawingDocNoSettingsApplied.totalPages),
    [drawingDocNoSettingsApplied.prefix, drawingDocNoSettingsApplied.totalPages]
  );

  const generatedDrawingDocNo = useMemo(
    () => buildGeneratedDrawingDocNo(form.projectNumber, drawingDocNoSheetCodes, drawingDocNoSelections),
    [drawingDocNoSelections, drawingDocNoSheetCodes, form.projectNumber]
  );

  const isFormDirty = useMemo(
    () => serializeCertificateEditorForm(form) !== baselineFormJson,
    [baselineFormJson, form]
  );

  const clearFeedback = () => {
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    setCertificateApproval(null);
    setCertificateApprovalCode('');
  }, [baselineFormJson, form, storedDraftPdf?.draftFileId, confirmForm.pdfFile]);

  useEffect(() => {
    let active = true;

    const loadInitialData = async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        setPractitionerOptionsBusy(true);
        setBuildingSurveyorOptionsBusy(true);
        setBuildingClassificationOptionsBusy(true);
        const [certificates, practitionerResponse, buildingSurveyorResponse, buildingClassificationResponse] = await Promise.all([
          azureSqlService.getCertificates({ page: 1, pageSize: 200 }),
          azureSqlService.getCertificatePractitionerOptions(),
          azureSqlService.getCertificateBuildingSurveyorOptions(),
          azureSqlService.getCertificateBuildingClassificationOptions(),
        ]);
        if (!active) return;
        setCertificateOptions(Array.isArray(certificates) ? certificates : []);

        const practitionerRows = Array.isArray(practitionerResponse?.data)
          ? practitionerResponse.data
          : Array.isArray(practitionerResponse)
            ? practitionerResponse
            : [];
        const normalizedPractitioners = practitionerRows
          .map((row: any) => {
            const rawName = String(row?.name || row?.Name || '').trim();
            const email = String(row?.email || row?.Email || '').trim().toLowerCase() || null;
            const name = rawName;
            if (!name) return null;
            return {
              name,
              email,
              address: resolvePractitionerAddress(
                String(row?.address || row?.Address || row?.postalAddress || row?.PostalAddress || '').trim() || null,
                String(row?.company || row?.Company || '').trim() || null
              ) || null,
              company: String(row?.company || row?.Company || '').trim() || null,
              registrationCategoryClass: resolvePractitionerRegistrationCategoryClass(
                String(row?.registrationCategoryClass || row?.RegistrationCategoryClass || row?.registrationCategory || row?.RegistrationCategory || row?.categoryClass || row?.CategoryClass || row?.registrationClass || row?.RegistrationClass || '').trim() || null
              ) || null,
              registrationNumber: String(row?.registrationNumber || row?.RegistrationNumber || '').trim() || null,
              sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
            } as CertificatePractitionerOption;
          })
          .filter((row: CertificatePractitionerOption | null): row is CertificatePractitionerOption => Boolean(row));
        setPractitionerOptions(normalizedPractitioners);
        setPractitionerOptionsMessage(
          normalizedPractitioners.length > 0
            ? ''
            : 'No named certificate engineers were found in CertificatePractitionerList or dbo.Users yet.'
        );

        const buildingSurveyorRows = Array.isArray(buildingSurveyorResponse?.data)
          ? buildingSurveyorResponse.data
          : Array.isArray(buildingSurveyorResponse)
            ? buildingSurveyorResponse
            : [];
        const normalizedBuildingSurveyors = buildingSurveyorRows
          .map((row: any) => {
            const name = String(row?.name || row?.Name || '').trim();
            if (!name) return null;
            return {
              name,
              email: String(row?.email || row?.Email || '').trim().toLowerCase() || null,
              postalAddress: String(row?.postalAddress || row?.PostalAddress || '').trim() || null,
              postcode: String(row?.postcode || row?.Postcode || row?.PostalCode || '').trim() || null,
              sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
            } as CertificateBuildingSurveyorOption;
          })
          .filter((row: CertificateBuildingSurveyorOption | null): row is CertificateBuildingSurveyorOption => Boolean(row));
        setBuildingSurveyorOptions(normalizedBuildingSurveyors);
        setBuildingSurveyorOptionsMessage(normalizedBuildingSurveyors.length > 0 ? '' : 'No saved building surveyors were found in the database.');

        const buildingClassificationRows = Array.isArray(buildingClassificationResponse?.data)
          ? buildingClassificationResponse.data
          : Array.isArray(buildingClassificationResponse)
            ? buildingClassificationResponse
            : [];
        const normalizedBuildingClassificationOptions = dedupeBuildingClassificationOptions(
          buildingClassificationRows
            .map((row: any) => {
              const partOfBuilding = String(row?.partOfBuilding || row?.PartOfBuilding || '').trim();
              const bcaClassification = String(row?.bcaClassification || row?.BcaClassification || row?.BCAClassification || '').trim();
              if (!partOfBuilding || !bcaClassification) return null;
              return {
                partOfBuilding,
                bcaClassification,
                sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
              } as BuildingClassificationOption;
            })
            .filter((row: BuildingClassificationOption | null): row is BuildingClassificationOption => Boolean(row))
        );
        setBuildingClassificationOptions(normalizedBuildingClassificationOptions);
        setBuildingClassificationMessage('');
      } catch (err: any) {
        if (!active) return;
        setError(formatUserFacingError(
          err,
          'Certificate workflow data could not load.',
          'You can keep editing visible fields, then retry loading reference data.'
        ));
        setPractitionerOptions([]);
        setPractitionerOptionsMessage('Practitioner options are unavailable right now.');
        setBuildingSurveyorOptions([]);
        setBuildingSurveyorOptionsMessage('Building surveyor options are unavailable right now.');
        setBuildingClassificationOptions([]);
        setBuildingClassificationMessage('Saved building options are unavailable right now, but the certificate rows below still remain editable.');
      } finally {
        if (active) {
          setPractitionerOptionsBusy(false);
          setBuildingSurveyorOptionsBusy(false);
          setBuildingClassificationOptionsBusy(false);
        }
      }
    };

    loadInitialData();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!practitionerOptions.length) return;

    setForm((current) => {
      const practitioner = findBestPractitionerOption(
        practitionerOptions,
        current.engineerName || current.engineerEmail || signedInEngineerName
      );
      if (!practitioner) return current;

      const currentName = String(current.engineerName || '').trim();
      const currentNameNeedsRepair = looksLikePractitionerNamePlaceholder(currentName, practitioner.company || '');
      const currentAddress = String(current.engineerAddress || '').trim();
      const currentAddressLooksLikeCompany = Boolean(
        currentAddress
        && practitioner.company
        && currentAddress.toLowerCase() === practitioner.company.trim().toLowerCase()
      );
      const nextName = !currentName || currentNameNeedsRepair
        ? practitioner.name
        : current.engineerName;
      const nextEmail = current.engineerEmail || practitioner.email || '';
      const nextAddress = !currentAddress || currentAddressLooksLikeCompany
        ? practitioner.address || ''
        : current.engineerAddress;
      const nextRegistrationCategoryClass = current.engineerRegistrationCategoryClass || practitioner.registrationCategoryClass || '';
      const nextRegistrationNumber = current.engineerRegistrationNumber || practitioner.registrationNumber || '';

      if (
        nextName === current.engineerName
        && nextEmail === current.engineerEmail
        && nextAddress === current.engineerAddress
        && nextRegistrationCategoryClass === current.engineerRegistrationCategoryClass
        && nextRegistrationNumber === current.engineerRegistrationNumber
      ) {
        return current;
      }

      return {
        ...current,
        engineerName: nextName,
        engineerEmail: nextEmail,
        engineerAddress: nextAddress,
        engineerRegistrationCategoryClass: nextRegistrationCategoryClass,
        engineerRegistrationNumber: nextRegistrationNumber,
      };
    });
  }, [practitionerOptions, signedInEngineerName]);

  useEffect(() => {
    if (!buildingSurveyorOptions.length) return;

    setForm((current) => {
      const surveyor = findBestBuildingSurveyorOption(
        buildingSurveyorOptions,
        current.buildingSurveyor || current.buildingSurveyorEmail
      );
      if (!surveyor) return current;

      const nextName = current.buildingSurveyor || surveyor.name;
      const nextEmail = current.buildingSurveyorEmail || surveyor.email || '';
      const nextPostalAddress = current.buildingSurveyorPostalAddress || surveyor.postalAddress || '';
      const nextPostcode = current.buildingSurveyorPostcode || surveyor.postcode || '';

      if (
        nextName === current.buildingSurveyor
        && nextEmail === current.buildingSurveyorEmail
        && nextPostalAddress === current.buildingSurveyorPostalAddress
        && nextPostcode === current.buildingSurveyorPostcode
      ) {
        return current;
      }

      return {
        ...current,
        buildingSurveyor: nextName,
        buildingSurveyorEmail: nextEmail,
        buildingSurveyorPostalAddress: nextPostalAddress,
        buildingSurveyorPostcode: nextPostcode,
      };
    });
  }, [buildingSurveyorOptions]);

  useEffect(() => {
    if (!availableBuildingClassificationOptions.length) return;

    setForm((current) => {
      const nextRows = current.buildingClassifications.map((row) => {
        if (row.bcaClassification.trim() || !row.partOfBuilding.trim()) {
          return row;
        }
        const bcaClassification = resolveBuildingClassificationValue(
          availableBuildingClassificationOptions,
          row.partOfBuilding,
          ''
        );
        return bcaClassification
          ? { ...row, bcaClassification }
          : row;
      });

      const changed = nextRows.some((row, index) => row.bcaClassification !== current.buildingClassifications[index]?.bcaClassification);
      if (!changed) return current;
      return {
        ...current,
        buildingClassifications: nextRows,
      };
    });
  }, [availableBuildingClassificationOptions]);

  useEffect(() => {
    const previousAutoPreparedBy = autoPreparedByRef.current;
    const nextAutoPreparedBy = preferredPreparedBy || fallbackPreparedBy;

    setForm((current) => {
      const nextDocuments = current.documents.map((row) => {
        const normalizedPreparedBy = row.preparedBy.trim();
        if (normalizedPreparedBy && normalizedPreparedBy !== previousAutoPreparedBy) {
          return row;
        }
        if (normalizedPreparedBy === nextAutoPreparedBy) {
          return row;
        }
        return {
          ...row,
          preparedBy: nextAutoPreparedBy,
        };
      });

      const changed = nextDocuments.some((row, index) => row.preparedBy !== current.documents[index]?.preparedBy);
      if (!changed) return current;
      return {
        ...current,
        documents: nextDocuments,
      };
    });

    autoPreparedByRef.current = nextAutoPreparedBy;
  }, [preferredPreparedBy]);

  useEffect(() => {
    setForm((current) => {
      const nextDocuments = current.documents.map((row, index) => {
        if (!isComputationDocumentRow(row)) {
          return row;
        }

        const nextAutoName = buildAutoComputationDocumentName(current, row.documentDate);
        if (!nextAutoName) {
          return row;
        }

        const previousAutoName = autoComputationDocumentNamesRef.current[index] || '';
        const currentDocumentName = row.documentNo.trim();
        if (currentDocumentName && currentDocumentName !== previousAutoName) {
          return row;
        }

        if (currentDocumentName === nextAutoName) {
          return row;
        }

        return {
          ...row,
          documentNo: nextAutoName,
        };
      });

      autoComputationDocumentNamesRef.current = nextDocuments.map((row) => (
        isComputationDocumentRow(row)
          ? buildAutoComputationDocumentName(current, row.documentDate)
          : ''
      ));

      const changed = nextDocuments.some((row, index) => row.documentNo !== current.documents[index]?.documentNo);
      if (!changed) return current;
      return {
        ...current,
        documents: nextDocuments,
      };
    });
  }, [form.documents, form.matterType, form.projectAddress, form.projectNumber, form.projectSuburb]);

  useEffect(() => {
    if (form.certificatePurpose === 'inspection') return;
    const latestDocumentDate = resolveLatestDocumentDate(form.documents);
    if (!latestDocumentDate) return;

    setForm((current) => {
      const currentLatestDate = resolveLatestDocumentDate(current.documents);
      if (!currentLatestDate) return current;
      const nextVersionTag = shouldAutoReplaceVersionTag(current.versionTag)
        ? formatCompactDate(currentLatestDate)
        : current.versionTag;
      if (
        current.issueDate === currentLatestDate &&
        current.engineerIssueDate === currentLatestDate &&
        current.versionTag === nextVersionTag
      ) {
        return current;
      }
      return {
        ...current,
        issueDate: currentLatestDate,
        engineerIssueDate: currentLatestDate,
        versionTag: nextVersionTag,
      };
    });
  }, [form.documents, form.certificatePurpose]);

  useEffect(() => {
    if (mode !== 'create') return undefined;
    const query = projectQuery.trim();
    if (query.length < 2) {
      setProjectOptions([]);
      setProjectLookupBusy(false);
      setProjectLookupMessage('');
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        setProjectLookupBusy(true);
        setProjectLookupMessage('');
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchProjects(query, 8, { lifecycle: 'all' });
        if (active) {
          const nextMatches = Array.isArray(matches) ? matches : [];
          setProjectOptions(nextMatches);
          setProjectLookupMessage(nextMatches.length > 0 ? '' : 'No saved project matches that lookup.');
        }
      } catch (err: any) {
        if (active) {
          setProjectOptions([]);
          setProjectLookupMessage(err?.status === 401
            ? 'Sign in again to search projects.'
            : 'Project lookup is unavailable right now.');
        }
      } finally {
        if (active) {
          setProjectLookupBusy(false);
        }
      }
    }, 180);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [mode, projectQuery]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(australianStandardStorageKey, JSON.stringify(dedupeDropdownOptions(australianStandardOptions)));
  }, [australianStandardOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isFormDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  useEffect(() => {
    const suburb = form.projectSuburb.trim();
    if (suburb.length < 2) {
      setSuburbOptions([]);
      if (!suburb) {
        setSuburbLookupMessage('');
      }
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchSuburbOptions(suburb, 8);
        if (!active) return;
        setSuburbOptions(Array.isArray(matches) ? matches : []);

        const exactMatch = (Array.isArray(matches) ? matches : []).find(
          (row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(suburb)
        );
        if (!exactMatch) return;

        setForm((current) => ({
          ...current,
          projectSuburb: exactMatch.Suburb || current.projectSuburb,
          projectPostcode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
            ? String(exactMatch.PostCode)
            : current.projectPostcode,
          projectCouncil: exactMatch.Council || current.projectCouncil,
        }));
        setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
      } catch {
        if (!active) return;
        setSuburbOptions([]);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [form.projectSuburb]);

  useEffect(() => {
    const state = (location.state || {}) as { certificateId?: string | number; certificateNumber?: string; mode?: WorkflowMode };
    if (!state.certificateId && !state.certificateNumber) {
      return;
    }

    setMode(state.mode === 'create' ? 'create' : 'amend');
    loadDraft({
      certificateId: state.certificateId ? String(state.certificateId) : undefined,
      certificateNumber: state.certificateNumber,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (field: keyof CertificateEditorForm, value: string | boolean) => {
    setForm((current) => {
      if (field === 'issueDate' || field === 'engineerIssueDate') {
        const nextDate = (current.certificatePurpose === 'inspection' ? '' : resolveLatestDocumentDate(current.documents)) || normalizeDateInputValue(value);
        if (!nextDate) return { ...current, [field]: value };
        return {
          ...current,
          issueDate: nextDate,
          engineerIssueDate: nextDate,
          versionTag: shouldAutoReplaceVersionTag(current.versionTag)
            ? formatCompactDate(nextDate)
            : current.versionTag,
        };
      }
      if (field === 'certificatePurpose') {
        const purpose = value === 'inspection' ? 'inspection' : 'design';
        return { ...current, certificatePurpose: purpose,
          certificateNumber: replaceCertificateKindSuffix(current.certificateNumber, current.matterType, purpose),
          certificateType: purpose === 'inspection' ? 'Certificate of Compliance for Building Work' : 'Certificate of Compliance',
          declarationCompetent: true,
          declarationInspectedPersonally: true, declarationInspectedWorkComplies: true,
        };
      }
      if (field === 'matterType') {
        const nextMatterType = String(value || STRUCTURAL_MATTER);
        return {
          ...current,
          matterType: nextMatterType,
          certificateNumber: replaceCertificateKindSuffix(current.certificateNumber, nextMatterType, current.certificatePurpose),
          documents: retargetDefaultDocumentTypes(current.documents, nextMatterType, preferredPreparedBy),
        };
      }
      return { ...current, [field]: value };
    });
  };

  const updateBuildingClassification = (index: number, field: keyof BuildingClassificationRow, value: string) => {
    setForm((current) => ({
      ...current,
      buildingClassifications: updateListRow(current.buildingClassifications, index, {
        ...current.buildingClassifications[index],
        ...(field === 'partOfBuilding'
          ? (() => {
              return {
                partOfBuilding: value,
                bcaClassification: resolveBuildingClassificationValue(
                  availableBuildingClassificationOptions,
                  value,
                  current.buildingClassifications[index]?.bcaClassification || ''
                ),
              };
            })()
          : { [field]: value }),
      }),
    }));
  };

  const updateDocument = (index: number, field: keyof DocumentRow, value: string) => {
    setForm((current) => ({
      ...current,
      documents: updateListRow(current.documents, index, {
        ...current.documents[index],
        ...(field === 'section'
          ? {
              section: value,
              documentType:
                current.documents[index]?.documentType
                || getDefaultDocumentType(value, current.matterType),
            }
          : { [field]: field === 'numberOfPages' ? normalizeDocumentPages(value) : value }),
      }),
    }));
  };

  const updatePerformanceRequirement = (index: number, field: keyof PerformanceRequirementRow, value: string) => {
    setForm((current) => ({
      ...current,
      performanceRequirements: updateListRow(current.performanceRequirements, index, {
        ...current.performanceRequirements[index],
        [field]: value,
      }),
    }));
  };

  const updateNccReference = (index: number, value: string) => {
    setForm((current) => ({
      ...current,
      bcaVersion: index === 0 ? value : current.bcaVersion,
      nccReferences: updateListRow(current.nccReferences, index, {
        ...current.nccReferences[index],
        version: value,
      }),
    }));
  };

  const updateBcaVersion = (value: string) => {
    setForm((current) => {
      const nextReferences = current.nccReferences.length
        ? updateListRow(current.nccReferences, 0, {
            ...current.nccReferences[0],
            version: value,
          })
        : [{ version: value }];

      return {
        ...current,
        bcaVersion: value,
        nccReferences: nextReferences,
      };
    });
  };

  const updateEngineerName = (value: string) => {
    setForm((current) => {
      const practitioner = findBestPractitionerOption(practitionerOptions, value);
      if (!String(value || '').trim()) {
        return {
          ...current,
          engineerName: value,
        };
      }
      return {
        ...current,
        engineerName: practitioner ? practitioner.name : value,
        engineerEmail: practitioner ? practitioner.email || '' : current.engineerEmail,
        engineerAddress: practitioner ? practitioner.address || '' : current.engineerAddress,
        engineerRegistrationCategoryClass: practitioner ? practitioner.registrationCategoryClass || '' : current.engineerRegistrationCategoryClass,
        engineerRegistrationNumber: practitioner ? practitioner.registrationNumber || '' : current.engineerRegistrationNumber,
      };
    });
  };

  const updateBuildingSurveyorName = (value: string) => {
    setForm((current) => {
      const surveyor = findBestBuildingSurveyorOption(buildingSurveyorOptions, value);
      if (!String(value || '').trim()) {
        return {
          ...current,
          buildingSurveyor: value,
        };
      }
      return {
        ...current,
        buildingSurveyor: surveyor ? surveyor.name : value,
        buildingSurveyorEmail: surveyor && !current.buildingSurveyorEmail ? surveyor.email || '' : current.buildingSurveyorEmail,
        buildingSurveyorPostalAddress: surveyor && !current.buildingSurveyorPostalAddress ? surveyor.postalAddress || '' : current.buildingSurveyorPostalAddress,
        buildingSurveyorPostcode: surveyor && !current.buildingSurveyorPostcode ? surveyor.postcode || '' : current.buildingSurveyorPostcode,
      };
    });
  };

  const updateProvision = (index: number, value: string) => {
    setForm((current) => ({
      ...current,
      provisions: updateListRow(current.provisions, index, {
        ...current.provisions[index],
        sectionProvision: value,
      }),
    }));
  };

  const hydrateProjectSuburbLookup = async (rawSuburb: string) => {
    const suburb = rawSuburb.trim();
    if (suburb.length < 2) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptions(suburb, 8);
      const options = Array.isArray(matches) ? matches : [];
      setSuburbOptions(options);

      const exactMatch = options.find((row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(suburb));
      if (!exactMatch) {
        setSuburbLookupMessage('No exact suburb match found yet. Keep typing or pick from the list.');
        return;
      }

      setForm((current) => ({
        ...current,
        projectSuburb: exactMatch.Suburb || current.projectSuburb,
        projectPostcode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
          ? String(exactMatch.PostCode)
          : current.projectPostcode,
        projectCouncil: exactMatch.Council || current.projectCouncil,
      }));
      setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const hydrateProjectPostCodeLookup = async (rawPostCode: string) => {
    const postCode = normalizePostCode(rawPostCode);
    if (postCode.length < 3) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptionsByPostCode(postCode, 20);
      const options = Array.isArray(matches) ? matches : [];
      setSuburbOptions(options);

      if (options.length === 0) {
        setSuburbLookupMessage('No suburb match found for that postcode yet.');
        return;
      }

      const exactSuburbMatch = options.find(
        (row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(form.projectSuburb)
      );
      const selectedMatch = exactSuburbMatch || (options.length === 1 ? options[0] : null);

      if (!selectedMatch) {
        setForm((current) => ({
          ...current,
          projectPostcode: postCode,
        }));
        setSuburbLookupMessage('Multiple suburbs match that postcode. Pick the suburb from the list.');
        return;
      }

      setForm((current) => ({
        ...current,
        projectSuburb: selectedMatch.Suburb || current.projectSuburb,
        projectPostcode: selectedMatch.PostCode !== null && selectedMatch.PostCode !== undefined
          ? String(selectedMatch.PostCode)
          : postCode,
        projectCouncil: selectedMatch.Council || current.projectCouncil,
      }));
      setSuburbLookupMessage(`Suburb${selectedMatch.Council ? ', postcode, and council' : ' and postcode'} filled from lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const addAustralianStandardOption = () => {
    const normalized = newAustralianStandard.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      setAustralianStandardMessage('Enter an Australian Standard before adding it.');
      return;
    }

    const nextOptions = dedupeDropdownOptions([...australianStandardOptions, normalized]);
    setAustralianStandardOptions(nextOptions);
    setNewAustralianStandard('');
    setForm((current) => ({
      ...current,
      provisions: ensureProvisionValues(current.provisions, [normalized]),
    }));
    setAustralianStandardMessage(nextOptions.some((option) => option === normalized)
      ? 'Australian Standard saved for reuse in this browser and checked for this certificate.'
      : '');
  };

  const toggleAustralianStandard = (value: string, checked: boolean) => {
    setForm((current) => ({
      ...current,
      provisions: checked
        ? ensureProvisionValues(current.provisions, [value])
        : removeProvisionValue(current.provisions, value),
    }));
  };

  const autoFillAustralianStandardsFromApi = async () => {
    try {
      setBusy('Auto-filling Australian Standards...');
      setAustralianStandardMessage('');
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.autoFillCertificateAustralianStandards({
        matterType: form.matterType,
        snapshot: buildSnapshotFromForm(form),
        buildingClassifications: form.buildingClassifications,
      }) as CertificateAustralianStandardsAutoFillResponse;
      const standards = Array.isArray(response?.standards)
        ? response.standards.map((value) => String(value || '').replace(/\s+/g, ' ').trim()).filter(Boolean)
        : [];
      const bcaVersion = String(response?.bcaVersion || '').replace(/\s+/g, ' ').trim();

      if (!standards.length && !bcaVersion) {
        setAustralianStandardMessage('No Australian Standards were recommended for the current certificate details.');
        return;
      }

      setForm((current) => ({
        ...current,
        bcaVersion: bcaVersion || current.bcaVersion,
        nccReferences: bcaVersion ? [{ version: bcaVersion }] : current.nccReferences,
        provisions: replaceProvisionValues(standards),
      }));

      const reasonText = Array.isArray(response?.reasons)
        ? response.reasons.map((value) => String(value || '').trim()).filter(Boolean).join(' ')
        : '';
      setAustralianStandardMessage(
        reasonText
        || `Auto-filled ${standards.length} standard${standards.length === 1 ? '' : 's'} and updated the NCC/BCA version from the legacy certificate macro rules.`
      );
    } catch (err: any) {
      setAustralianStandardMessage(err?.message || 'Australian Standards could not be auto-filled right now.');
    } finally {
      setBusy(null);
    }
  };

  const addBuildingClassificationRowFromHelper = () => {
    const partOfBuilding = newBuildingPartOption.replace(/\s+/g, ' ').trim();
    const bcaClassification = newBuildingClassOption.replace(/\s+/g, ' ').trim();
    if (!partOfBuilding || !bcaClassification) {
      setBuildingClassificationMessage('Enter both a building type and a BCA classification before adding a row.');
      return;
    }

    setForm((current) => {
      const nextRow = { partOfBuilding, bcaClassification };
      const blankIndex = current.buildingClassifications.findIndex((row) => !row.partOfBuilding.trim() && !row.bcaClassification.trim());
      const nextRows = blankIndex >= 0
        ? updateListRow(current.buildingClassifications, blankIndex, nextRow)
        : [...current.buildingClassifications, nextRow];

      return {
        ...current,
        buildingClassifications: nextRows,
      };
    });
    setNewBuildingPartOption('');
    setNewBuildingClassOption('');
    setBuildingClassificationMessage('Helper values added to the editable building classification rows below.');
  };

  const addBuildingClassificationOption = async () => {
    const partOfBuilding = newBuildingPartOption.replace(/\s+/g, ' ').trim();
    const bcaClassification = newBuildingClassOption.replace(/\s+/g, ' ').trim();
    if (!partOfBuilding || !bcaClassification) {
      setBuildingClassificationMessage('Enter both a part of building and a BCA classification before saving.');
      return;
    }

    const nextOption = { partOfBuilding, bcaClassification, sourceTable: 'dbo.CertificateBuildingClassificationOptionList' } as BuildingClassificationOption;
    const alreadyAvailable = availableBuildingClassificationOptions.some((option) =>
      normalizeLookupLabel(option.partOfBuilding) === normalizeLookupLabel(partOfBuilding)
      && normalizeLookupLabel(option.bcaClassification) === normalizeLookupLabel(bcaClassification)
    );

    try {
      setBusy('Saving building option...');
      setBuildingClassificationMessage('');
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.saveCertificateBuildingClassificationOption({
        partOfBuilding,
        bcaClassification,
      });
      const saved = response?.data || response || nextOption;
      const savedOption = {
        partOfBuilding: String(saved?.partOfBuilding || saved?.PartOfBuilding || partOfBuilding).trim(),
        bcaClassification: String(saved?.bcaClassification || saved?.BcaClassification || saved?.BCAClassification || bcaClassification).trim(),
        sourceTable: String(saved?.sourceTable || saved?.SourceTable || nextOption.sourceTable || '').trim() || null,
      } as BuildingClassificationOption;
      setBuildingClassificationOptions((current) => dedupeBuildingClassificationOptions([...current, savedOption]));
      setNewBuildingPartOption('');
      setNewBuildingClassOption('');
      setBuildingClassificationMessage(alreadyAvailable
        ? 'Building option is already available for reuse.'
        : 'Building option saved for reuse.');
    } catch (err: any) {
      const errorMessage = String(err?.message || '').trim();
      setBuildingClassificationMessage(
        /migration/i.test(errorMessage)
          ? `${errorMessage} The building classification rows below still stay editable and save with the certificate draft.`
          : errorMessage || 'Building option could not be saved right now.'
      );
    } finally {
      setBusy(null);
    }
  };

  const copyTextToClipboard = async (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    if (typeof document === 'undefined') {
      throw new Error('Clipboard access is unavailable in this environment.');
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', 'true');
    textArea.style.position = 'absolute';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (!copied) {
      throw new Error('Clipboard access is unavailable right now.');
    }
  };

  const refreshDrawingDocNoMatrix = (settings = drawingDocNoSettingsDraft, existingSelections: Record<string, string> = {}) => {
    const normalizedSettings = normalizeDrawingDocNoSettings(settings);
    const revisionTokens = parseRevisionSequence(normalizedSettings.revisionSequenceInput);
    const sheetCodes = buildDrawingSheetCodes(normalizedSettings.prefix, normalizedSettings.totalPages);
    setDrawingDocNoSettingsDraft(normalizedSettings);
    setDrawingDocNoSettingsApplied(normalizedSettings);
    setDrawingDocNoSelections(buildDrawingRevisionSelections(sheetCodes, revisionTokens, existingSelections));
    setDrawingDocNoMessage('');
  };

  const openDrawingDocNoGenerator = () => {
    const prefix = getDefaultDrawingPrefix(form.matterType);
    const exactTarget = form.documents.find((row) => isDrawingDocumentTypeTarget(row.documentType, prefix));
    const fallbackTarget = form.documents.find((row) => row.section.trim().toLowerCase() === 'drawing');
    const targetRow = exactTarget || fallbackTarget || null;
    const nextSettings = normalizeDrawingDocNoSettings({
      prefix,
      totalPages: parseFinalPageNumber(targetRow?.numberOfPages) || '1',
      revisionSequenceInput: getDefaultRevisionSequenceInput(prefix.startsWith('C') ? DRAINAGE_MATTER : STRUCTURAL_MATTER),
    });
    const revisionTokens = parseRevisionSequence(nextSettings.revisionSequenceInput);
    const sheetCodes = buildDrawingSheetCodes(nextSettings.prefix, nextSettings.totalPages);
    setDrawingDocNoSettingsDraft(nextSettings);
    setDrawingDocNoSettingsApplied(nextSettings);
    setDrawingDocNoSelections(buildDrawingRevisionSelections(sheetCodes, revisionTokens));
    setDrawingDocNoMessage('');
    setDrawingDocNoGeneratorOpen(true);
  };

  const updateDrawingDocNoSelection = (sheetCode: string, revision: string) => {
    setDrawingDocNoSelections((current) => ({
      ...current,
      [sheetCode]: current[sheetCode] === revision ? '' : revision,
    }));
    setDrawingDocNoMessage('');
  };

  const copyGeneratedDrawingDocNo = async () => {
    if (!generatedDrawingDocNo.trim()) {
      setDrawingDocNoMessage('Select at least one revision so there is something to copy.');
      return false;
    }
    try {
      await copyTextToClipboard(generatedDrawingDocNo);
      setDrawingDocNoMessage('Generated drawing doc number copied to the clipboard.');
      return true;
    } catch (err: any) {
      setDrawingDocNoMessage(err?.message || 'Clipboard copy failed.');
      return false;
    }
  };

  const applyGeneratedDrawingDocNo = async (closeAfterApply = false) => {
    if (!generatedDrawingDocNo.trim()) {
      setDrawingDocNoMessage('Select at least one revision so the drawing doc number can be applied.');
      return;
    }

    const targetMatterType = drawingDocNoSettingsApplied.prefix.startsWith('C') ? DRAINAGE_MATTER : STRUCTURAL_MATTER;
    const targetDocumentType = getDefaultDocumentType('Drawing', targetMatterType);
    const normalizedPages = normalizeDocumentPages(drawingDocNoSettingsApplied.totalPages);

    setForm((current) => {
      const exactIndexes = current.documents
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => {
          const normalizedSection = row.section.trim().toLowerCase();
          return (normalizedSection === 'drawing' || row.documentType.toLowerCase().includes('drawing'))
            && isDrawingDocumentTypeTarget(row.documentType, drawingDocNoSettingsApplied.prefix);
        })
        .map(({ index }) => index);
      const fallbackIndexes = exactIndexes.length
        ? exactIndexes
        : current.documents
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row.section.trim().toLowerCase() === 'drawing')
          .slice(0, 1)
          .map(({ index }) => index);

      if (fallbackIndexes.length) {
        return {
          ...current,
          documents: current.documents.map((row, index) => {
            if (!fallbackIndexes.includes(index)) return row;
            const keepDocumentType = exactIndexes.includes(index) && row.documentType.trim();
            return {
              ...row,
              section: 'Drawing',
              documentNo: generatedDrawingDocNo,
              documentDate: row.documentDate || getTodayDateInput(),
              documentType: keepDocumentType ? row.documentType : targetDocumentType,
              numberOfPages: normalizedPages,
              preparedBy: row.preparedBy || preferredPreparedBy,
            };
          }),
        };
      }

      return {
        ...current,
        documents: [
          ...current.documents,
          {
            ...createPresetDocumentRow('Drawing', targetMatterType, preferredPreparedBy),
            documentNo: generatedDrawingDocNo,
            numberOfPages: normalizedPages,
          },
        ],
      };
    });

    const copied = await copyGeneratedDrawingDocNo();
    if (closeAfterApply) {
      setDrawingDocNoGeneratorOpen(false);
      setDrawingDocNoMessage('');
    }
    setSuccess(copied
      ? 'Applied the generated drawing doc number to supporting documents and copied it to the clipboard.'
      : 'Applied the generated drawing doc number to supporting documents.');
  };

  const selectProject = (project: ProjectOption) => {
    const projectNumber = project.ProjectCode || '';
    setProjectQuery(projectNumber);
    setProjectOptions([]);
    setProjectLookupBusy(false);
    setProjectLookupMessage('');
    setSuburbLookupMessage('');
    setForm((current) => ({
      ...current,
      projectId: String(project.ProjectId || ''),
      projectNumber,
      projectAddress: project.SiteAddress || current.projectAddress,
      projectSuburb: project.Suburb || current.projectSuburb,
      projectPostcode: normalizePostCode(project.PostCode) || current.projectPostcode,
      projectCouncil: project.Council || current.projectCouncil,
      title: current.title || project.ProjectName || current.title,
      certificateNumber: current.certificateNumber || `${projectNumber} ${getCertificateFileKind(current.matterType, current.certificatePurpose)}`,
    }));
    clearFeedback();
  };

  const loadLastCertificateData = async () => {
    const projectNumber = String(form.projectNumber || projectQuery).trim();
    if (!projectNumber) {
      setError('Select a project before loading previous certificate data.');
      return;
    }

    const disciplineLabel = isDrainageMatter(form.matterType) ? 'civil' : 'structural';
    setBusy('load-last-certificate');
    clearFeedback();
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      let candidate = findLatestCertificateTemplate(certificateOptions, projectNumber, form.matterType, form.certificatePurpose);

      if (!candidate) {
        const searchedCertificates = await azureSqlService.getCertificates({
          page: 1,
          pageSize: 50,
          query: { q: projectNumber },
          useCache: false,
        });
        const searchedOptions = Array.isArray(searchedCertificates) ? searchedCertificates : [];
        setCertificateOptions((current) => mergeCertificateOptions(current, searchedOptions));
        candidate = findLatestCertificateTemplate(searchedOptions, projectNumber, form.matterType, form.certificatePurpose);
      }

      if (!candidate) {
        setError(`No previous ${disciplineLabel} certificate data was found for project ${projectNumber}.`);
        return;
      }

      const draft = await azureSqlService.getWorkflowCertificateDraft({
        certificateId: candidate.CertificateId ? String(candidate.CertificateId) : undefined,
        certificateNumber: candidate.CertificateNumber,
      }) as CertificateDraftResponse;
      const templateForm = mapDraftToForm(draft);
      const nextCertificateNumber = form.certificateNumber.trim() || `${projectNumber} ${getCertificateFileKind(form.matterType, form.certificatePurpose)}`;
      const nextForm: CertificateEditorForm = {
        ...templateForm,
        certificatePurpose: form.certificatePurpose,
        certificateType: form.certificatePurpose === 'inspection' ? 'Certificate of Compliance for Building Work' : templateForm.certificateType,
        inspectionDate: '',
        buildingWorkInspected: '',
        inspectionFindings: '',
        inspectionLimitations: '',
        declarationInspectedPersonally: true,
        declarationInspectedWorkComplies: true,
        declarationCompetent: form.certificatePurpose === 'inspection' ? true : templateForm.declarationCompetent,
        certificateId: '',
        certificateNumber: nextCertificateNumber,
        projectId: form.projectId || templateForm.projectId,
        projectNumber,
        projectFolderName: form.projectFolderName || templateForm.projectFolderName,
        title: form.title || templateForm.title,
        issueDate: form.issueDate,
        status: 'Draft',
        versionTag: form.versionTag,
        projectAddress: form.projectAddress || templateForm.projectAddress,
        projectSuburb: form.projectSuburb || templateForm.projectSuburb,
        projectPostcode: form.projectPostcode || templateForm.projectPostcode,
        projectCouncil: form.projectCouncil || templateForm.projectCouncil,
        matterType: form.matterType,
        engineerIssueDate: form.engineerIssueDate,
      };

      setForm(nextForm);
      setMode('create');
      setSourceKind(draft.source || null);
      setStoredDraftPdf(null);
      setConfirmForm({ certificateId: '', pdfFile: null });
      setHistoryLookupId('');
      setVersions(null);
      setResult(null);
      setCertificateApproval(null);
      setCertificateApprovalCode('');
      setSuccess(`Loaded reusable data from ${candidate.CertificateNumber || `the last ${disciplineLabel} certificate`} into a new ${disciplineLabel} certificate. The previous certificate remains unchanged.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, `The last ${disciplineLabel} certificate data could not load.`));
    } finally {
      setBusy(null);
    }
  };

  const resetForCreate = () => {
    const nextForm = createNewCertificateForm();
    const practitioner = findBestPractitionerOption(practitionerOptions, signedInEngineerName);
    if (practitioner) {
      nextForm.engineerName = practitioner.name;
      nextForm.engineerEmail = practitioner.email || '';
      nextForm.engineerAddress = practitioner.address || '';
      nextForm.engineerRegistrationCategoryClass = practitioner.registrationCategoryClass || '';
      nextForm.engineerRegistrationNumber = practitioner.registrationNumber || '';
    }
    setMode('create');
    setForm(nextForm);
    setBaselineFormJson(serializeCertificateEditorForm(nextForm));
    setStoredDraftPdf(null);
    setSourceKind(null);
    setProjectQuery('');
    setProjectOptions([]);
    setProjectLookupBusy(false);
    setProjectLookupMessage('');
    setCertificateLookup('');
    setConfirmForm({ certificateId: '', pdfFile: null });
    setHistoryLookupId('');
    setVersions(null);
    setResult(null);
    setSuburbOptions([]);
    setSuburbLookupMessage('');
    setAustralianStandardMessage('');
    clearFeedback();
  };

  const requestResetForCreate = () => {
    if (isFormDirty) {
      const confirmed = window.confirm('Save the current certificate draft before starting a new cert? Unsaved changes will be lost if you continue.');
      if (!confirmed) return;
    }
    resetForCreate();
  };

  const loadDraft = async ({ certificateId, certificateNumber }: { certificateId?: string; certificateNumber?: string }) => {
    const normalizedId = String(certificateId || '').trim();
    const normalizedNumber = String(certificateNumber || '').trim();
    if (!normalizedId && !normalizedNumber) {
      setError('Enter a certificate number or select a certificate to load.');
      return null;
    }

    setBusy('load-draft');
    clearFeedback();
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const draft = await azureSqlService.getWorkflowCertificateDraft({
        certificateId: normalizedId || undefined,
        certificateNumber: normalizedNumber || undefined,
      }) as CertificateDraftResponse;

      const nextForm = mapDraftToForm(draft);
      setForm(nextForm);
      setMode('amend');
      setSourceKind(draft.source || null);
      setStoredDraftPdf(draft.storedDraftPdf || null);
      setSuburbLookupMessage('');
      setAustralianStandardMessage('');
      setCertificateLookup(nextForm.certificateNumber);
      setProjectQuery(nextForm.projectNumber);
      setConfirmForm({ certificateId: nextForm.certificateId, pdfFile: null });
      setHistoryLookupId(nextForm.certificateId);
      setBaselineFormJson(serializeCertificateEditorForm(nextForm));
      if (nextForm.certificateId) {
        try {
          const nextVersions = await azureSqlService.getCertificateWorkflowVersions(nextForm.certificateId);
          setVersions(Array.isArray(nextVersions) ? nextVersions : []);
        } catch {
          setVersions([]);
        }
      } else {
        setVersions([]);
      }
      setSuccess(`Loaded certificate ${nextForm.certificateNumber || normalizedId} into the editor.`);
      return { form: nextForm, draft };
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Certificate draft could not load.'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const loadDraftFromLookup = async () => {
    const normalizedLookup = certificateLookup.trim();
    if (!normalizedLookup) {
      setError('Enter a certificate number or project number to load.');
      return;
    }

    const candidate = resolveCertificateLookupCandidate(certificateOptions, normalizedLookup);
    if (candidate.match) {
      setCertificateLookup(candidate.match.CertificateNumber || normalizedLookup);
      await loadDraft({
        certificateId: candidate.match.CertificateId ? String(candidate.match.CertificateId) : undefined,
        certificateNumber: candidate.match.CertificateNumber || normalizedLookup,
      });
      return;
    }

    if (candidate.ambiguous) {
      setError(`More than one certificate matches "${normalizedLookup}". Pick the exact certificate from the dropdown list.`);
      return;
    }

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const searchedCertificates = await azureSqlService.getCertificates({
        page: 1,
        pageSize: 20,
        query: { q: normalizedLookup },
        useCache: false,
      });
      const searchedOptions = Array.isArray(searchedCertificates) ? searchedCertificates : [];
      if (searchedOptions.length > 0) {
        setCertificateOptions((current) => mergeCertificateOptions(current, searchedOptions));
        const searchedCandidate = resolveCertificateLookupCandidate(searchedOptions, normalizedLookup);
        if (searchedCandidate.match) {
          setCertificateLookup(searchedCandidate.match.CertificateNumber || normalizedLookup);
          await loadDraft({
            certificateId: searchedCandidate.match.CertificateId ? String(searchedCandidate.match.CertificateId) : undefined,
            certificateNumber: searchedCandidate.match.CertificateNumber || normalizedLookup,
          });
          return;
        }

        if (searchedCandidate.ambiguous) {
          setError(`More than one certificate matches "${normalizedLookup}". Pick the exact certificate from the dropdown list.`);
          return;
        }
      }
    } catch {
      // Fall back to the direct draft load below if the search endpoint is unavailable.
    }

    await loadDraft({ certificateNumber: normalizedLookup });
  };

  const renderStoreAndOpenDraftPdf = async (
    certificateId: string,
    formOverride?: CertificateEditorForm,
    previewWindow?: Window | null,
  ) => {
    const targetForm = formOverride || form;
    const normalizedCertificateId = String(certificateId || '').trim();
    if (!normalizedCertificateId) {
      throw new Error('Certificate ID is required before rendering a draft PDF.');
    }

    const { azureSqlService } = await import('@/services/azureSqlService.js');
    await azureSqlService.amendWorkflowCertificate({
      certificateId: normalizedCertificateId,
      certificateNumber: targetForm.certificateNumber || undefined,
      projectId: targetForm.projectId || undefined,
      certificateType: targetForm.certificateType || undefined,
      title: targetForm.title || undefined,
      issueDate: resolveCertificateDateFromForm(targetForm) || undefined,
      status: targetForm.status || undefined,
      notes: targetForm.notes || undefined,
      versionTag: resolveCertificateVersionTag(targetForm),
      snapshot: buildSnapshotFromForm(targetForm),
    });
    setBaselineFormJson(serializeCertificateEditorForm(targetForm));
    const renderResponse = await azureSqlService.renderWorkflowCertificate({
      certificateId: normalizedCertificateId,
      snapshot: buildSnapshotFromForm(targetForm),
      pdfFileName: buildDraftPdfFileName(targetForm),
      renderStyle: activeCertificateRenderStyle,
    });

    const pdfBase64 = renderResponse?.pdfBase64 || renderResponse?.data?.pdfBase64;
    const pdfContentType = renderResponse?.pdfContentType || renderResponse?.data?.pdfContentType || 'application/pdf';
    const fileName = renderResponse?.fileName || renderResponse?.data?.fileName || buildDraftPdfFileName(targetForm);
    if (!pdfBase64) {
      throw new Error('Render preview did not return a PDF payload.');
    }

    const pdfBlob = base64ToBlob(pdfBase64, pdfContentType);
    const uploaded = await azureSqlService.uploadDraftFile({
      entityType: 'certificate-pdf-draft',
      recordId: normalizedCertificateId,
      file: pdfBlob,
      contentType: pdfContentType,
      fileName,
    });

    const linked = await azureSqlService.linkWorkflowCertificateDraftPdf({
      certificateId: normalizedCertificateId,
      draftFileId: uploaded?.fileId,
      pdfFileName: fileName,
      pdfContentType,
      contentPath: uploaded?.contentPath,
    });

    const nextStoredDraft = linked?.storedDraftPdf || linked?.data?.storedDraftPdf || null;
    setStoredDraftPdf(nextStoredDraft);
    setResult(renderResponse);
    openPdfBlob(pdfBlob, previewWindow);
    await loadVersions(normalizedCertificateId);
    return nextStoredDraft;
  };

  const previewCertificatePdf = async () => {
    clearFeedback();
    setBusy('preview-render');
    const previewWindow = openPendingPdfPreviewWindow();
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const renderResponse = await azureSqlService.renderWorkflowCertificate({
        certificateId: form.certificateId.trim() || undefined,
        certificateNumber: form.certificateNumber || undefined,
        certificateType: form.certificateType || undefined,
        title: form.title || undefined,
        sourceKind: sourceKind || undefined,
        snapshot: buildSnapshotFromForm(form),
        pdfFileName: buildDraftPdfFileName(form),
        renderStyle: activeCertificateRenderStyle,
      });
      const pdfBase64 = renderResponse?.pdfBase64 || renderResponse?.data?.pdfBase64;
      const pdfContentType = renderResponse?.pdfContentType || renderResponse?.data?.pdfContentType || 'application/pdf';
      if (!pdfBase64) {
        throw new Error('Render preview did not return a PDF payload.');
      }
      openPdfBlob(base64ToBlob(pdfBase64, pdfContentType), previewWindow);
      setSuccess(`Opened Cert126 preview PDF for ${form.certificateNumber || form.projectNumber || 'this draft'}.`);
    } catch (err: any) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setError(formatUserFacingError(err, 'Certificate PDF preview could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const reviewStoredDraftPdf = async () => {
    if (!storedDraftPdf?.draftFileId) {
      setError('No stored draft PDF is linked to this certificate yet.');
      return;
    }

    clearFeedback();
    setBusy('review-draft');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const download = await azureSqlService.downloadDraftFile(storedDraftPdf.draftFileId);
      openPdfBlob(download.blob);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Stored draft PDF could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const saveStoredDraftPdfToSharePoint = async () => {
    const certificateId = form.certificateId.trim();
    if (!certificateId) {
      setError('Save or load a certificate before saving the draft PDF to SharePoint.');
      return;
    }
    if (!storedDraftPdf?.draftFileId) {
      setError('Render and store the draft PDF before saving it to SharePoint.');
      return;
    }

    clearFeedback();
    setBusy('draft-sharepoint');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.saveWorkflowCertificateDraftPdfToSharePoint({
        certificateId,
        draftFileId: storedDraftPdf.draftFileId,
        pdfFileName: storedDraftPdf.fileName || undefined,
      });
      setResult(response);
      const upload = response?.sharePointUpload || response?.data?.sharePointUpload || null;
      setSuccess(`Saved draft PDF to SharePoint: ${upload?.plannedPath || upload?.webUrl || storedDraftPdf.fileName || 'construction package folder'}.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Draft PDF could not be saved to SharePoint.'));
    } finally {
      setBusy(null);
    }
  };

  const submitCertificate = async (event: React.FormEvent) => {
    event.preventDefault();
    clearFeedback();

    if (!form.projectNumber.trim()) {
      setError('Project number is required before saving the certificate draft.');
      return;
    }
    if (mode === 'create' && !form.projectId.trim()) {
      setError('Select a project from Project lookup before saving a new certificate draft.');
      return;
    }

    setBusy(mode === 'create' ? 'create' : 'amend');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const certificateDate = resolveCertificateDateFromForm(form);
      const versionTag = resolveCertificateVersionTag(form);
      const payload = {
        certificateId: mode === 'amend' ? form.certificateId || undefined : undefined,
        certificateNumber: form.certificateNumber || undefined,
        projectId: form.projectId || undefined,
        certificateType: form.certificateType || undefined,
        title: form.title || undefined,
        issueDate: certificateDate || undefined,
        status: form.status || undefined,
        notes: form.notes || undefined,
        versionTag,
        snapshot: buildSnapshotFromForm(form),
      };

      const response = mode === 'create'
        ? await azureSqlService.createWorkflowCertificate(payload)
        : await azureSqlService.amendWorkflowCertificate(payload);

      const responseCertificateId = String(response?.certificateId || response?.data?.certificateId || '');
      const responseCertificateNumber = String(response?.certificateNumber || response?.data?.certificateNumber || form.certificateNumber);
      const responseCertificateTable = String(response?.certificateTable || response?.data?.certificateTable || '').trim();
      const responseVersionTable = String(response?.versionTable || response?.data?.versionTable || '').trim();
      const saveTargetSummary = [responseCertificateTable, responseVersionTable].filter(Boolean).join(' + ');
      const nextForm = {
        ...form,
        certificateId: responseCertificateId || form.certificateId,
        certificateNumber: responseCertificateNumber || form.certificateNumber,
      };

      setResult(response);
      setForm(nextForm);
      setBaselineFormJson(serializeCertificateEditorForm(nextForm));
      setMode('amend');
      setSourceKind((response?.source || response?.data?.source || sourceKind) as 'current' | 'civil' | null);
      setConfirmForm({ certificateId: responseCertificateId, pdfFile: null });
      setHistoryLookupId(responseCertificateId || form.certificateId);
      setBuildingSurveyorOptions((current) => mergeBuildingSurveyorOption(current, {
        name: nextForm.buildingSurveyor,
        email: nextForm.buildingSurveyorEmail || null,
        postalAddress: nextForm.buildingSurveyorPostalAddress || null,
        postcode: nextForm.buildingSurveyorPostcode || null,
        sourceTable: responseCertificateTable || 'dbo.CertificateBuildingSurveyorList',
      }));
      if (nextForm.buildingSurveyor.trim()) {
        setBuildingSurveyorOptionsMessage(`Saved surveyor lookup entries come from dbo.CertificateBuildingSurveyorList. Latest draft save wrote certificate rows to ${saveTargetSummary || 'the configured certificate tables'}.`);
      }

      if (responseCertificateId) {
        const reloadResult = await loadDraft({ certificateId: responseCertificateId });
        if (!reloadResult) {
          setSuccess(mode === 'create'
            ? `Created certificate ${responseCertificateNumber || nextForm.certificateNumber}. Draft rows saved to ${saveTargetSummary || 'the configured certificate tables'}, but the page could not refresh the draft from the server automatically.`
            : `Saved amendments to certificate ${responseCertificateNumber || nextForm.certificateNumber}. Draft rows saved to ${saveTargetSummary || 'the configured certificate tables'}, but the page could not refresh the draft from the server automatically.`);
          return;
        }
      }

      setSuccess(mode === 'create'
        ? `Created certificate ${responseCertificateNumber || nextForm.certificateNumber}. Draft rows saved to ${saveTargetSummary || 'the configured certificate tables'}. Use Preview Cert126 PDF or Render + Store Draft PDF when you are ready.`
        : `Saved amendments to certificate ${responseCertificateNumber || nextForm.certificateNumber}. Draft rows saved to ${saveTargetSummary || 'the configured certificate tables'}. Use Preview Cert126 PDF or Render + Store Draft PDF when needed.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, mode === 'create' ? 'Certificate draft could not be created.' : 'Certificate amendments could not be saved.'));
    } finally {
      setBusy(null);
    }
  };

  const buildCertificateIssuePdfPayload = async () => {
    const certificateId = confirmForm.certificateId.trim() || form.certificateId.trim();
    if (!certificateId) {
      throw new Error('Save or load a certificate before requesting approval.');
    }

    if (form.certificatePurpose === 'inspection') {
      if (!form.inspectionDate || !form.buildingWorkInspected.trim() || !form.engineerName.trim() || !form.engineerRegistrationNumber.trim() || !form.engineerRegistrationCategoryClass.trim()) {
        throw new Error('Complete the inspection date, inspected work and engineer registration details before requesting approval.');
      }
      if (!form.declarationInspectedPersonally || !form.declarationInspectedWorkComplies || !form.declarationCompetent) {
        throw new Error('Confirm all three inspection declarations before requesting approval.');
      }
    }
    const { azureSqlService } = await import('@/services/azureSqlService.js');
    if (confirmForm.pdfFile) {
      return {
        certificateId,
        pdfBase64: await fileToBase64(confirmForm.pdfFile),
        pdfFileName: confirmForm.pdfFile.name,
        pdfContentType: confirmForm.pdfFile.type || 'application/pdf',
      };
    }

    // Approval must cover the current editor values, not a previously stored PDF.
    const renderResponse = await azureSqlService.renderWorkflowCertificate({
      certificateId,
      certificateNumber: form.certificateNumber || undefined,
      certificateType: form.certificateType || undefined,
      title: form.title || undefined,
      sourceKind: sourceKind || undefined,
      snapshot: buildSnapshotFromForm(form),
      pdfFileName: buildDraftPdfFileName(form),
      renderStyle: activeCertificateRenderStyle,
    });
    const pdfBase64 = renderResponse?.pdfBase64 || renderResponse?.data?.pdfBase64;
    if (!pdfBase64) {
      throw new Error('Render preview did not return a PDF payload for approval.');
    }
    return {
      certificateId,
      pdfBase64,
      pdfFileName: renderResponse?.fileName || renderResponse?.data?.fileName || buildDraftPdfFileName(form),
      pdfContentType: renderResponse?.pdfContentType || renderResponse?.data?.pdfContentType || 'application/pdf',
    };
  };

  const requestCertificateIssueApproval = async () => {
    clearFeedback();
    setBusy('issue-approval');
    try {
      const payload = await buildCertificateIssuePdfPayload();
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const approval = await azureSqlService.requestDocumentApproval({
        documentType: 'certificate',
        documentId: payload.certificateId,
        documentNumber: form.certificateNumber || undefined,
        pdfBase64: payload.pdfBase64,
      });
      setCertificateApproval({
        approvalId: String(approval?.approvalId || ''),
        status: String(approval?.status || 'Pending'),
        approverEmail: approval?.approverEmail || null,
        expiresAtLabel: approval?.expiresAtLabel || null,
        approvedAt: approval?.approvedAt || null,
        pdfBase64: payload.pdfBase64,
        pdfFileName: payload.pdfFileName,
        pdfContentType: payload.pdfContentType,
      });
      setCertificateApprovalCode('');
      setSuccess(`Approval code sent${approval?.approverEmail ? ` to ${approval.approverEmail}` : ''}.`);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Certificate approval request could not be sent.'));
    } finally {
      setBusy(null);
    }
  };

  const verifyCertificateIssueApproval = async () => {
    clearFeedback();
    if (!certificateApproval?.approvalId) {
      setError('Request certificate approval before entering the code.');
      return;
    }
    if (!certificateApprovalCode.trim()) {
      setError('Enter the approval code from the email.');
      return;
    }

    setBusy('issue-approval-verify');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const verified = await azureSqlService.verifyDocumentApproval({
        approvalId: certificateApproval.approvalId,
        otpCode: certificateApprovalCode.trim(),
      });
      setCertificateApproval((prev) => prev ? {
        ...prev,
        status: String(verified?.status || 'Approved'),
        approvedAt: verified?.approvedAt || new Date().toISOString(),
        approverEmail: verified?.approverEmail || prev.approverEmail,
        expiresAtLabel: verified?.expiresAtLabel || prev.expiresAtLabel,
      } : prev);
      setCertificateApprovalCode('');
      setSuccess('Certificate digital signing approval verified. Review once more, then confirm issue.');
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Certificate approval code could not be verified.'));
    } finally {
      setBusy(null);
    }
  };

  const confirmAndIssue = async (event: React.FormEvent) => {
    event.preventDefault();
    clearFeedback();

    const certificateId = confirmForm.certificateId.trim() || form.certificateId.trim();
    if (!certificateId) {
      setError('Save or load a certificate before confirming issue.');
      return;
    }
    if (!certificateApproval?.approvalId || certificateApproval.status !== 'Approved' || !certificateApproval.pdfBase64) {
      setError('Request Email OTP approval and verify the code before confirming the digitally signed certificate.');
      return;
    }

    setBusy('issue');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const payload: Record<string, unknown> = {
        certificateId,
        approvalId: certificateApproval.approvalId,
        pdfBase64: certificateApproval.pdfBase64,
        pdfFileName: certificateApproval.pdfFileName,
        pdfContentType: certificateApproval.pdfContentType,
        notes: form.notes || undefined,
        versionTag: resolveCertificateVersionTag(form),
        snapshot: buildSnapshotFromForm(form),
      };

      const response = await azureSqlService.issueLockWorkflowCertificate(payload);
      setResult(response);
      setSuccess(`Issued certificate ${response?.certificateNumber || form.certificateNumber} and copied it to the construction package folder.`);
      setCertificateApproval(null);
      setCertificateApprovalCode('');
      await loadDraft({ certificateId });
      await loadVersions(certificateId);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Certificate could not be confirmed and issued.'));
    } finally {
      setBusy(null);
    }
  };

  const loadVersions = async (certificateId = historyLookupId) => {
    const normalizedId = String(certificateId || '').trim();
    if (!normalizedId) {
      setError('Certificate ID is required to load version history.');
      return;
    }

    setBusy('versions');
    setHistoryLookupId(normalizedId);
    setError(null);
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const data = await azureSqlService.getCertificateWorkflowVersions(normalizedId);
      setVersions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Certificate version history could not load.'));
    } finally {
      setBusy(null);
    }
  };

  const workflowChips = [
    {
      label: 'Create / Amend',
      detail: form.projectNumber ? `Project ${form.projectNumber}` : 'Choose a project',
      status: form.certificateId ? 'Working' : form.projectNumber ? 'Ready' : 'Draft',
    },
    {
      label: 'Render Draft',
      detail: storedDraftPdf?.fileName || (form.certificateId ? 'Ready to render or store' : 'Save or load a certificate first'),
      status: storedDraftPdf?.draftFileId ? 'Stored' : form.certificateId ? 'Ready' : 'Pending',
    },
    {
      label: 'Confirm',
      detail: result?.data?.sharePointUpload?.plannedPath || (form.certificateId ? 'Ready to confirm into construction package' : 'Construction package pending'),
      status: result?.workflow === 'certificate-issue-lock' ? 'Issued' : form.certificateId ? 'Ready' : 'Pending',
    },
    {
      label: 'History',
      detail: historyLookupId
        ? versions && versions.length > 0
          ? `Certificate ${historyLookupId}`
          : `No versions yet for certificate ${historyLookupId}`
        : 'Load version history',
      status: versions && versions.length > 0 ? 'Loaded' : historyLookupId ? 'Ready' : 'Pending',
    },
  ];

  const activeCertificateRenderStyle = useProposedCertificateStyle ? proposedCertificateRenderStyle : undefined;
  const advancedSnapshot = useMemo(() => JSON.stringify(buildSnapshotFromForm(form), null, 2), [form]);
  const splitSheetColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(320px, 1fr) minmax(320px, 1fr)';
  const splitEqualColumns = isPhone ? 'minmax(0, 1fr)' : '1fr 1fr';
  const splitHeaderFields = 'repeat(2, minmax(0, 1fr))';
  const buildingOptionColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(0, 1.25fr) minmax(0, 0.75fr) auto';
  const classificationRowColumns = isPhone ? 'minmax(0, 1fr)' : '1fr 1fr 90px';
  const nccRowColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto';
  const documentHeaderColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(150px, 170px) minmax(0, 1fr)';
  const documentMetaColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(140px, 0.85fr) minmax(180px, 1.2fr) minmax(120px, 0.7fr) minmax(170px, 0.9fr) 90px';
  const performanceRowColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 90px';
  const australianStandardsColumns = isPhone ? 'minmax(0, 1fr)' : '1fr auto';
  const staffStatusColumns = isPhone ? 'minmax(0, 1fr)' : '1fr 0.85fr';
  const lowerPanelColumns = isPhone ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(320px, 1fr))';
  const modalColumns = isPhone ? 'minmax(0, 1fr)' : 'minmax(300px, 0.75fr) minmax(520px, 1.25fr)';
  const modalSettingsColumns = isPhone ? 'minmax(0, 1fr)' : '130px 1fr';
  const stackedActionDirection = isPhone ? 'column' : 'row';
  const compactPanelPadding = isPhone ? 14 : 18;
  const sheetShellPadding = isPhone ? 14 : 20;
  const responsivePageButtonPrimary = isPhone ? { ...pageButtonPrimary, width: '100%', justifyContent: 'center' } : pageButtonPrimary;
  const responsivePageButtonSecondary = isPhone ? { ...pageButtonSecondary, width: '100%', justifyContent: 'center' } : pageButtonSecondary;
  const responsivePageButtonDark = isPhone ? { ...pageButtonDark, width: '100%', justifyContent: 'center' } : pageButtonDark;
  const responsivePageButtonSuccess = isPhone ? { ...pageButtonSuccess, width: '100%', justifyContent: 'center' } : pageButtonSuccess;
  const responsivePageButtonAddRow = isPhone ? { ...pageButtonAddRow, width: '100%', justifyContent: 'center' } : pageButtonAddRow;
  const responsivePageButtonTeal = isPhone ? { ...pageButtonTeal, width: '100%', justifyContent: 'center' } : pageButtonTeal;
  const responsivePageButtonDanger = isPhone ? { ...pageButtonDanger, width: '100%', justifyContent: 'center' } : pageButtonDanger;
  const certificateApprovalIsApproved = certificateApproval?.status === 'Approved';
  const certificateApprovalHasRequest = Boolean(certificateApproval?.approvalId);
  const certificateApprovalStatusLabel = certificateApprovalIsApproved ? 'Approved' : certificateApprovalHasRequest ? 'OTP sent' : 'Approval required';
  const certificateApprovalStatusMessage = certificateApprovalIsApproved
    ? `Approved${certificateApproval?.approverEmail ? ` by ${certificateApproval.approverEmail}` : ''}.`
    : certificateApprovalHasRequest
      ? `OTP sent${certificateApproval?.approverEmail ? ` to ${certificateApproval.approverEmail}` : ''}${certificateApproval?.expiresAtLabel ? `, expires ${certificateApproval.expiresAtLabel}` : ''}.`
      : 'Request Email OTP approval before confirming the digitally signed certificate.';
  const certificateRenderStyleToggle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minHeight: 38,
    border: useProposedCertificateStyle ? '1px solid rgba(35,78,168,0.42)' : '1px solid #dbe4f0',
    borderRadius: 10,
    padding: '8px 12px',
    background: useProposedCertificateStyle ? 'rgba(234,241,255,0.96)' : '#fff',
    color: '#0f172a',
    fontWeight: 800,
    fontSize: 13,
    width: isPhone ? '100%' : undefined,
    boxSizing: 'border-box',
  };

  return (
    <div className="wdgf-page wdgf-page--certificate-workflow">
      <div className="wdgf-page__hero">
        <div className="wdgf-page__hero-inner">
          <div className="wdgf-page__hero-row">
            <div className="wdgf-page__hero-title">
              <button type="button" onClick={() => navigate('/certificates')} className="wdgf-page__back-button">
                Back
              </button>
              <div>
                <div className="wdgf-page__hero-kicker">Certificate Workflow</div>
                <h1 style={{ margin: 0, fontSize: isPhone ? 22 : 26, lineHeight: 1.1 }}>{isPhone ? 'Certificate workflow' : 'Edit certificate drafts in place, then confirm into construction packages'}</h1>
              </div>
            </div>

            <div className="wdgf-mode-switch wdgf-certificate-mode-switch" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: isPhone ? '100%' : undefined }}>
              <button
                type="button"
                onClick={resetForCreate}
                className={`wdgf-page__mode-button${mode === 'create' ? ' wdgf-page__mode-button--active' : ''}`}
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => { setMode('amend'); clearFeedback(); }}
                className={`wdgf-page__mode-button${mode === 'amend' ? ' wdgf-page__mode-button--active' : ''}`}
              >
                Amend
              </button>
              <StatusBadge status={busy ? 'In Progress' : 'Active'} />
            </div>
          </div>

          <div className="wdgf-page__hero-copy">
            Save the working certificate draft on the sheet below, render and store the PDF draft in blob storage, then confirm the final issue into the project&apos;s SharePoint construction package folder.
          </div>

          <div className="wdgf-certificate-workflow-chips" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {workflowChips.map((chip) => (
              <div key={chip.label} className="wdgf-certificate-workflow-chip" style={{ padding: '14px 16px', borderRadius: 16, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.16)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontWeight: 700 }}>{chip.label}</div>
                  <StatusBadge status={chip.status} size="sm" />
                </div>
                <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,0.88)' }}>{chip.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wdgf-page__content">
        <nav className="wdgf-mobile-jumpnav" aria-label="Certificate workflow sections">
          <a href="#certificate-draft">Draft</a>
          <a href="#certificate-classifications">Classify</a>
          <a href="#certificate-documents">Docs</a>
          <a href="#certificate-confirm">Confirm</a>
        </nav>

        {error ? (
          <div className="wdgf-notice wdgf-notice--danger">
            <div style={{ fontWeight: 700 }}>Workflow error</div>
            <div style={{ marginTop: 8 }}>{error}</div>
          </div>
        ) : null}

        {success ? (
          <div className="wdgf-notice wdgf-notice--success">
            <div style={{ fontWeight: 700 }}>Workflow update</div>
            <div style={{ marginTop: 8 }}>{success}</div>
          </div>
        ) : null}

        <div id="certificate-draft" className="wdgf-panel wdgf-certificate-lookup-panel" style={{ padding: compactPanelPadding, display: 'grid', gap: 16 }}>
          <div className="wdgf-certificate-lookup-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e3a8a', marginBottom: 6 }}>Project lookup</label>
              <input style={inputBase} placeholder="Type project number, address, or name" value={projectQuery} onChange={(event) => setProjectQuery(event.target.value)} />
              {mode === 'create' ? (
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => { void loadLastCertificateData(); }}
                    disabled={Boolean(busy) || !String(form.projectNumber || '').trim()}
                    style={responsivePageButtonSecondary}
                  >
                    {busy === 'load-last-certificate'
                      ? 'Loading last cert data...'
                      : `Load last ${isDrainageMatter(form.matterType) ? 'civil' : 'structural'} ${form.certificatePurpose === 'inspection' ? 'inspection ' : ''}cert data`}
                  </button>
                  <div style={{ fontSize: 12, lineHeight: 1.4, color: '#64748b' }}>
                    Copies the latest matching certificate JSON into this new draft without amending the previous certificate.
                  </div>
                </div>
              ) : null}
              {mode === 'create' && projectOptions.length > 0 ? (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 6, background: '#fff', border: '1px solid rgba(148,163,184,0.22)', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', overflow: 'hidden' }}>
                  {projectOptions.map((project) => (
                    <button key={project.ProjectId} type="button" onClick={() => selectProject(project)} style={{ width: '100%', border: 'none', background: '#fff', padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{project.ProjectCode || `Project ${project.ProjectId}`}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>{project.SiteAddress || project.ProjectName || 'No project address'}</div>
                    </button>
                  ))}
                </div>
              ) : null}
              {mode === 'create' && projectOptions.length === 0 && projectQuery.trim().length >= 2 && (projectLookupBusy || projectLookupMessage) ? (
                <div style={{ marginTop: 6, fontSize: 12, color: projectLookupMessage ? '#64748b' : '#1e3a8a' }}>
                  {projectLookupMessage || 'Searching projects...'}
                </div>
              ) : null}
            </div>

            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e3a8a', marginBottom: 6 }}>Amend existing certificate</label>
              <div style={{ display: 'flex', gap: 8, flexDirection: stackedActionDirection }}>
                <input
                  style={inputBase}
                  placeholder="Type certificate no. or project no."
                  value={certificateLookup}
                  onChange={(event) => setCertificateLookup(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void loadDraftFromLookup();
                    }
                  }}
                />
                <button type="button" onClick={() => { void loadDraftFromLookup(); }} disabled={busy === 'load-draft'} style={responsivePageButtonPrimary}>
                  {busy === 'load-draft' ? 'Loading...' : 'Load'}
                </button>
              </div>
              {mode === 'amend' && filteredCertificateOptions.length > 0 ? (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 6, background: '#fff', border: '1px solid rgba(148,163,184,0.22)', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', overflow: 'hidden' }}>
                  {filteredCertificateOptions.map((certificate) => (
                    <button
                      key={String(certificate.CertificateId || certificate.CertificateNumber)}
                      type="button"
                      onClick={() => {
                        setCertificateLookup(certificate.CertificateNumber || '');
                        loadDraft({ certificateId: certificate.CertificateId ? String(certificate.CertificateId) : undefined, certificateNumber: certificate.CertificateNumber });
                      }}
                      style={{ width: '100%', border: 'none', background: '#fff', padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }}
                    >
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{certificate.CertificateNumber || `Certificate ${certificate.CertificateId}`}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {[
                          certificate.ProjectNumber ? `Project ${certificate.ProjectNumber}` : '',
                          certificate.ProjectLocation || certificate.CertificateType || 'No project details',
                        ].filter(Boolean).join(' • ')}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e3a8a', marginBottom: 6 }}>Certificate source</label>
              <div style={{ ...inputBase, minHeight: 42, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                {sourceKind === 'civil' ? 'Civil certificate source' : sourceKind === 'current' ? 'Current certificate source' : 'Will resolve from your account'}
              </div>
            </div>
          </div>

          <form className="wdgf-certificate-form" onSubmit={submitCertificate} style={{ display: 'grid', gap: 16 }}>
            <div className="wdgf-certificate-sheet-pad" style={{ padding: sheetShellPadding, background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)', borderRadius: 18 }}>
              <div className="wdgf-certificate-sheet-shell" style={{ border: '1px solid #dbe4f0', borderRadius: 20, overflow: 'hidden', background: '#fff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: splitSheetColumns, borderBottom: '1px solid #dbe4f0' }}>
                  <div className="wdgf-certificate-sheet-intro" style={{ padding: isPhone ? '16px 14px 14px' : '20px 18px 16px', borderRight: isPhone ? 'none' : '1px solid #dbe4f0', borderBottom: isPhone ? '1px solid #dbe4f0' : 'none', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', color: '#1e3a8a', textTransform: 'uppercase', marginBottom: 10 }}>Draft certification sheet</div>
                    <div style={{ fontSize: isPhone ? 28 : 34, fontWeight: 800, color: '#0f172a', lineHeight: 1.05 }}>{certificateHeroTitle}</div>
                    <div className="wdgf-certificate-sheet-intro-copy" style={{ marginTop: 10, color: '#334155', lineHeight: 1.55, maxWidth: 520 }}>
                      Create and amend on this same sheet, then render the blob-backed draft from the exact data you see here.
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: splitHeaderFields, borderBottom: '1px solid #dbe4f0' }}>
                      {[
                        { label: 'Certificate no.', field: 'certificateNumber' as const },
                        { label: 'Certificate Date', field: 'issueDate' as const },
                        { label: 'Status', field: 'status' as const },
                        { label: 'Revision', field: 'versionTag' as const },
                      ].map((column) => (
                        <div
                          key={column.field}
                          style={{
                            padding: '12px 14px',
                            borderLeft: isPhone
                              ? 'none'
                              : column.field === 'certificateNumber' || column.field === 'status'
                                ? 'none'
                                : '1px solid #dbe4f0',
                            borderTop: isPhone
                              ? column.field === 'certificateNumber' ? 'none' : '1px solid #dbe4f0'
                              : column.field === 'status' || column.field === 'versionTag'
                                ? '1px solid #dbe4f0'
                                : 'none',
                          }}
                        >
                          <div style={{ ...sheetLabel, marginBottom: 6 }}>{column.label}</div>
                          {column.field === 'status' ? (
                            <select style={{ ...inputBase, padding: '8px 10px' }} value={form.status} onChange={(event) => updateForm('status', event.target.value)}>
                              {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                          ) : (
                            <input style={{ ...sheetInput, fontSize: isPhone ? 16 : 18, fontWeight: 800 }} value={form[column.field]} onChange={(event) => updateForm(column.field, event.target.value)} type={column.field === 'issueDate' ? 'date' : 'text'} />
                          )}
                        </div>
                      ))}
                    </div>

                    <div style={{ padding: '14px 16px 16px', display: 'grid', gap: 6 }}>
                      <div style={{ ...sheetLabel, marginBottom: 0 }}>Certificate ID</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{form.certificateId || 'Pending until first save'}</div>
                      <div style={{ fontSize: 13, color: '#475569' }}>Final SharePoint name follows the project folder plus <strong>{getCertificateFileKind(form.matterType, form.certificatePurpose)}</strong> and the certificate date.</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: splitSheetColumns, borderBottom: '1px solid #dbe4f0' }}>
                  <div style={{ padding: compactPanelPadding, borderRight: isPhone ? 'none' : '1px solid #dbe4f0', borderBottom: isPhone ? '1px solid #dbe4f0' : 'none' }}>
                    <div style={{ ...sheetLabel }}>Project details</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <input style={{ ...sheetInput, fontSize: isPhone ? 19 : 22, fontWeight: 800 }} value={form.projectNumber} onChange={(event) => { updateForm('projectNumber', event.target.value); setProjectQuery(event.target.value); }} placeholder="Project number" />
                      <input style={sheetInput} value={form.title} onChange={(event) => updateForm('title', event.target.value)} placeholder="Project title / certificate title" />
                      <textarea style={sheetTextArea} value={form.projectAddress} onChange={(event) => updateForm('projectAddress', event.target.value)} placeholder="Project address" />
                      <input
                        style={sheetInput}
                        value={form.projectSuburb}
                        onChange={(event) => {
                          updateForm('projectSuburb', event.target.value);
                          setSuburbLookupMessage('');
                        }}
                        onBlur={(event) => { void hydrateProjectSuburbLookup(event.target.value); }}
                        placeholder="Suburb"
                        list="certificate-project-suburb-options"
                      />
                      <datalist id="certificate-project-suburb-options">
                        {suburbOptions.map((option) => (
                          <option key={`${option.Suburb}-${option.PostCode}`} value={option.Suburb}>
                            {normalizePostCode(option.PostCode)}{option.Council ? ` - ${option.Council}` : ''}
                          </option>
                        ))}
                      </datalist>
                      <div style={{ display: 'grid', gridTemplateColumns: splitEqualColumns, gap: 12 }}>
                        <input
                          style={sheetInput}
                          value={form.projectPostcode}
                          onChange={(event) => {
                            updateForm('projectPostcode', event.target.value);
                            setSuburbLookupMessage('');
                          }}
                          onBlur={(event) => { void hydrateProjectPostCodeLookup(event.target.value); }}
                          placeholder="Postcode"
                        />
                        <input style={sheetInput} value={form.projectCouncil} onChange={(event) => updateForm('projectCouncil', event.target.value)} placeholder="Municipal district / council" />
                      </div>
                      {suburbLookupMessage ? (
                        <div style={{ fontSize: 12, color: '#475569' }}>{suburbLookupMessage}</div>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ padding: compactPanelPadding, background: '#f8fafc' }}>
                    <div style={{ ...sheetLabel }}>Issue to and surveyor</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <input style={sheetInput} value={form.issuedToEntity} onChange={(event) => updateForm('issuedToEntity', event.target.value)} placeholder="Issued to entity" />
                      <input
                        style={sheetInput}
                        list="certificate-building-surveyor-name-options"
                        value={form.buildingSurveyor}
                        onChange={(event) => updateBuildingSurveyorName(event.target.value)}
                        placeholder="Relevant building surveyor"
                      />
                      <input
                        style={sheetInput}
                        list="certificate-building-surveyor-email-options"
                        value={form.buildingSurveyorEmail}
                        onChange={(event) => updateForm('buildingSurveyorEmail', event.target.value)}
                        placeholder="Surveyor email"
                      />
                      <textarea style={sheetTextArea} value={form.buildingSurveyorPostalAddress} onChange={(event) => updateForm('buildingSurveyorPostalAddress', event.target.value)} placeholder="Surveyor postal address" />
                      <input style={sheetInput} value={form.buildingSurveyorPostcode} onChange={(event) => updateForm('buildingSurveyorPostcode', event.target.value)} placeholder="Surveyor postcode" />
                    </div>
                    <datalist id="certificate-building-surveyor-name-options">
                      {availableBuildingSurveyorNameOptions.map((option) => <option key={option} value={option} />)}
                    </datalist>
                    <datalist id="certificate-building-surveyor-email-options">
                      {availableBuildingSurveyorEmailOptions.map((option) => <option key={option} value={option} />)}
                    </datalist>
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 10 }}>
                      {buildingSurveyorOptionsBusy
                        ? 'Loading saved building surveyors...'
                        : buildingSurveyorOptionsMessage || 'Saving a certificate draft now stores any new building surveyor details into the surveyor lookup table.'}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: splitSheetColumns, borderBottom: '1px solid #dbe4f0' }}>
                  <div style={{ padding: compactPanelPadding, borderRight: isPhone ? 'none' : '1px solid #dbe4f0', borderBottom: isPhone ? '1px solid #dbe4f0' : 'none' }}>
                    <div style={{ ...sheetLabel }}>Certificate matter</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={sheetLabel}>Certificate purpose</span>
                        <select style={inputBase} value={form.certificatePurpose} disabled={Boolean(form.certificateId)} onChange={(event) => updateForm('certificatePurpose', event.target.value)}>
                          <option value="design">Design — section 238(1)(a)</option>
                          <option value="inspection">Inspection — section 238(1)(b)</option>
                        </select>
                      </label>
                      {form.certificatePurpose === 'inspection' && <>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Prescribed class of building work</span>
                          <select style={inputBase} value={form.inspectionWorkClass} onChange={(event) => updateForm('inspectionWorkClass', event.target.value)}>
                            <option value="domestic">Domestic building work</option>
                            <option value="temporary-structure">Erection of a prescribed temporary structure</option>
                          </select>
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Inspection date</span>
                          <input style={sheetInput} type="date" value={form.inspectionDate} onChange={(event) => updateForm('inspectionDate', event.target.value)} />
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Building work inspected</span>
                          <textarea style={{ ...sheetTextArea, minHeight: 110 }} value={form.buildingWorkInspected} onChange={(event) => updateForm('buildingWorkInspected', event.target.value)} placeholder="Describe the part of the building work or inspection stage personally inspected." />
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Inspection observations / records</span>
                          <textarea style={{ ...sheetTextArea, minHeight: 90 }} value={form.inspectionFindings} onChange={(event) => updateForm('inspectionFindings', event.target.value)} placeholder="Record what was inspected, including members, connections and the stage of work. Optional." />
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Scope and limitations</span>
                          <textarea style={{ ...sheetTextArea, minHeight: 90 }} value={form.inspectionLimitations} onChange={(event) => updateForm('inspectionLimitations', event.target.value)} placeholder="Describe access limitations, concealed work or excluded areas, if applicable." />
                        </label>
                        <p style={{ margin: 0, color: '#475569', fontSize: 13 }}>These details appear on the certificate and are saved with this draft and its versions. Loading a previous certificate into a new draft clears the inspection date, work, observations and limitations. The three inspector declarations default to checked and can be changed before saving.</p>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Effective height</span>
                          <input style={sheetInput} value={form.effectiveHeight} onChange={(event) => updateForm('effectiveHeight', event.target.value)} placeholder="e.g. 6 m, if applicable" />
                        </label>
                        <p style={{ margin: 0, color: '#475569', fontSize: 13 }}>The certifying engineer must have personally inspected this work and hold the relevant building endorsement. Inspection and issue dates are independent of drawing dates.</p>
                      </>}
                      <input style={sheetInput} value={form.certificateType} onChange={(event) => updateForm('certificateType', event.target.value)} placeholder="Certificate type" />
                      <div className="wdgf-certificate-matter-selects" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                        <select style={{ ...inputBase, padding: '8px 10px' }} value={form.matterType} onChange={(event) => updateForm('matterType', event.target.value)}>
                          {matterTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                        <select style={{ ...inputBase, padding: '8px 10px' }} value={form.natureOfBuildingWorkType} onChange={(event) => updateForm('natureOfBuildingWorkType', event.target.value)}>
                          {natureOfBuildingWorkTypeOptions.map((option) => (
                            <option key={option || 'blank'} value={option}>
                              {option || 'Nature of building work'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Description of Construction</span>
                          <input
                            style={sheetInput}
                            value={form.natureOfBuildingWorkDescription}
                            onChange={(event) => updateForm('natureOfBuildingWorkDescription', event.target.value)}
                            placeholder="e.g. to Existing House"
                          />
                        </label>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
                          The PDF will render this as the selected work type followed by this description.
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Storeys contained</span>
                          <input style={sheetInput} value={form.storeysContained} onChange={(event) => updateForm('storeysContained', event.target.value)} placeholder="Storeys contained" />
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Rise in storeys</span>
                          <input style={sheetInput} value={form.riseInStoreys} onChange={(event) => updateForm('riseInStoreys', event.target.value)} placeholder="Rise in storeys" />
                        </label>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={sheetLabel}>Type of construction</span>
                          <select style={{ ...inputBase, padding: '8px 10px' }} value={form.typeOfConstruction} onChange={(event) => updateForm('typeOfConstruction', event.target.value)}>
                            <option value="">Type of construction</option>
                            {typeOfConstructionOptions.filter(Boolean).map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                        </label>
                      </div>
                      <select style={{ ...inputBase, padding: '8px 10px' }} value={form.bcaVersion} onChange={(event) => updateBcaVersion(event.target.value)}>
                        <option value="">Applicable BCA / NCC version</option>
                        {availableNccProvisionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        This field reuses the NCC dropdown list below and stays matched to the first NCC reference row.
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: compactPanelPadding, background: '#f8fafc' }}>
                    <div style={{ ...sheetLabel }}>Engineer details</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <select style={{ ...inputBase, padding: '8px 10px' }} value={form.engineerName} onChange={(event) => updateEngineerName(event.target.value)}>
                        <option value="">Engineer name</option>
                        {availablePractitionerSelectOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: -6 }}>
                        {practitionerOptionsBusy
                          ? 'Loading practitioner options from the database...'
                          : practitionerOptionsMessage || 'Selecting a named engineer fills any stored email, address, registration category, and registration number values. Saving a draft now stores those engineer details into CertificatePractitionerList for next time.'}
                      </div>
                      <input style={sheetInput} value={form.engineerEmail} onChange={(event) => updateForm('engineerEmail', event.target.value)} placeholder="Engineer email" />
                      <textarea style={sheetTextArea} value={form.engineerAddress} onChange={(event) => updateForm('engineerAddress', event.target.value)} placeholder="Engineer address" />
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={sheetLabel}>Registration category / class</span>
                        <input style={sheetInput} value={form.engineerRegistrationCategoryClass} onChange={(event) => updateForm('engineerRegistrationCategoryClass', event.target.value)} placeholder="Registration category / class" />
                      </label>
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={sheetLabel}>Registration number</span>
                        <input style={sheetInput} value={form.engineerRegistrationNumber} onChange={(event) => updateForm('engineerRegistrationNumber', event.target.value)} placeholder="Registration number" />
                      </label>
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={sheetLabel}>Engineer issue date</span>
                        <input style={sheetInput} type="date" value={form.engineerIssueDate} onChange={(event) => updateForm('engineerIssueDate', event.target.value)} />
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ padding: 18, borderBottom: '1px solid #dbe4f0' }}>
                  <div style={{ ...sheetLabel }}>Declaration checks</div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {form.certificatePurpose !== 'inspection' && <select
                      style={{ ...inputBase, padding: '8px 10px', maxWidth: isPhone ? '100%' : 340 }}
                      value={form.declarationPreparedDesign ? 'prepared' : 'not_prepared'}
                      onChange={(event) => updateForm('declarationPreparedDesign', event.target.value === 'prepared')}
                    >
                      <option value="prepared">Prepared the design</option>
                      <option value="not_prepared">Did not prepare the design</option>
                    </select>}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                    {(form.certificatePurpose === 'inspection' ? [
                      ['declarationInspectedPersonally', 'I personally inspected the building work'],
                      ['declarationInspectedWorkComplies', 'The work inspected by me complies with the listed provisions'],
                      ['declarationCompetent', 'I hold the skills, experience and knowledge to issue this certificate'],
                    ] : [
                      ['includePerformanceSolution', 'Include performance solution'],
                      ['declarationDesignComplies', 'Design complies'],
                      ['declarationPerformanceSolutionComplies', 'Performance solution complies'],
                      ['declarationCompetent', 'Competent to sign'],
                    ]).map(([field, label]) => (
                      <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #dbe4f0', borderRadius: 12, padding: '10px 12px', background: '#fff' }}>
                        <input type="checkbox" checked={Boolean(form[field as keyof CertificateEditorForm])} onChange={(event) => updateForm(field as keyof CertificateEditorForm, event.target.checked)} />
                        <span style={{ color: '#0f172a', fontWeight: 600 }}>{label}</span>
                      </label>
                    ))}
                    </div>
                  </div>
                </div>

                <details id="certificate-classifications" className="certificate-mobile-disclosure" open={isPhone ? undefined : true}>
                  <summary className="certificate-mobile-disclosure__summary">
                    Classifications and NCC references
                  </summary>
                  <div style={{ padding: 18, borderBottom: '1px solid #dbe4f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: splitEqualColumns, gap: 16 }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: 10 }}>
                        <div style={{ ...sheetLabel, marginBottom: 0 }}>Building classifications</div>
                        <button type="button" onClick={() => setForm((current) => ({ ...current, buildingClassifications: [...current.buildingClassifications, emptyBuildingClassification()] }))} style={responsivePageButtonAddRow}>
                          + Row
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: buildingOptionColumns, gap: 8, marginBottom: 10 }}>
                        <input
                          style={inputBase}
                          value={newBuildingPartOption}
                          onChange={(event) => {
                            const nextPartOfBuilding = event.target.value;
                            setNewBuildingPartOption(nextPartOfBuilding);
                            setNewBuildingClassOption(resolveBuildingClassificationValue(
                              availableBuildingClassificationOptions,
                              nextPartOfBuilding,
                              ''
                            ));
                            setBuildingClassificationMessage('');
                          }}
                          placeholder="Building type helper, e.g. Office"
                          list="certificate-building-part-options"
                        />
                        <input
                          style={inputBase}
                          value={newBuildingClassOption}
                          onChange={(event) => {
                            setNewBuildingClassOption(event.target.value);
                            setBuildingClassificationMessage('');
                          }}
                          placeholder="Suggested BCA classification"
                          list="certificate-building-class-options"
                        />
                        <div style={{ display: 'grid', gap: 8 }}>
                          <button type="button" onClick={addBuildingClassificationRowFromHelper} style={responsivePageButtonAddRow}>
                            Use helper
                          </button>
                          <button type="button" onClick={addBuildingClassificationOption} style={responsivePageButtonAddRow}>
                            Save option
                          </button>
                        </div>
                      </div>
                      {buildingClassificationOptionsBusy ? (
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Loading saved building options from the database...</div>
                      ) : buildingClassificationMessage ? (
                        <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>{buildingClassificationMessage}</div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                          Use the NCC helper for common building types, then add the suggestion into the rows below. Mixed-use projects usually need separate rows, and the rows below always remain free-edit.
                        </div>
                      )}
                      {buildingClassificationHelperMatch?.helperNote ? (
                        <div style={{ fontSize: 12, color: '#34579e', marginBottom: 10 }}>
                          {buildingClassificationHelperMatch.helperNote}
                        </div>
                      ) : null}
                      <datalist id="certificate-building-part-options">
                        {availableBuildingClassificationOptions.map((option) => (
                          <option key={`building-part-option-${option.partOfBuilding}-${option.bcaClassification}`} value={option.partOfBuilding} label={buildBuildingClassificationOptionLabel(option)}>
                            {buildBuildingClassificationOptionLabel(option)}
                          </option>
                        ))}
                      </datalist>
                      <datalist id="certificate-building-class-options">
                        {availableBuildingClassOptions.map((option) => <option key={`building-class-option-${option}`} value={option} />)}
                      </datalist>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {form.buildingClassifications.map((row, index) => (
                          <div key={`classification-${index}`} style={{ display: 'grid', gridTemplateColumns: classificationRowColumns, gap: 8, border: '1px solid #dbe4f0', borderRadius: 12, padding: 10, background: '#fff' }}>
                            <input
                              style={inputBase}
                              value={row.partOfBuilding}
                              onChange={(event) => updateBuildingClassification(index, 'partOfBuilding', event.target.value)}
                              placeholder="Part of building"
                              list="certificate-building-part-options"
                            />
                            <input
                              style={inputBase}
                              value={row.bcaClassification}
                              onChange={(event) => updateBuildingClassification(index, 'bcaClassification', event.target.value)}
                              placeholder="BCA classification"
                              list="certificate-building-class-options"
                            />
                            <button type="button" onClick={() => setForm((current) => ({ ...current, buildingClassifications: current.buildingClassifications.filter((_, rowIndex) => rowIndex !== index).length ? current.buildingClassifications.filter((_, rowIndex) => rowIndex !== index) : [emptyBuildingClassification()] }))} style={responsivePageButtonDanger}>
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: 10 }}>
                        <div style={{ ...sheetLabel, marginBottom: 0 }}>NCC references</div>
                        <button type="button" onClick={() => setForm((current) => ({ ...current, nccReferences: [...current.nccReferences, emptyNccReference()] }))} style={responsivePageButtonAddRow}>
                          + Row
                        </button>
                      </div>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {form.nccReferences.map((row, index) => (
                          <div key={`ncc-${index}`} style={{ display: 'grid', gridTemplateColumns: nccRowColumns, gap: 12, alignItems: 'center' }}>
                            <select style={inputBase} value={row.version} onChange={(event) => updateNccReference(index, event.target.value)}>
                              <option value="">NCC version / clause</option>
                              {availableNccProvisionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                            <button
                              type="button"
                              onClick={() => setForm((current) => {
                                const remaining = current.nccReferences.filter((_, rowIndex) => rowIndex !== index);
                                const nextRows = remaining.length ? remaining : [emptyNccReference()];
                                return {
                                  ...current,
                                  bcaVersion: nextRows.find((item) => item.version.trim())?.version || '',
                                  nccReferences: nextRows,
                                };
                              })}
                              style={responsivePageButtonDanger}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    </div>
                  </div>
                </details>

                <details id="certificate-documents" className="certificate-mobile-disclosure" open={isPhone ? undefined : true}>
                  <summary className="certificate-mobile-disclosure__summary">
                    {form.certificatePurpose === 'inspection' ? 'Referenced drawings and inspection records' : 'Supporting documents'}
                  </summary>
                  <div style={{ padding: 18, borderBottom: '1px solid #dbe4f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: 10 }}>
                    <div style={{ ...sheetLabel, marginBottom: 0 }}>{form.certificatePurpose === 'inspection' ? 'Referenced drawings and inspection records' : 'Supporting documents'}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={openDrawingDocNoGenerator} style={responsivePageButtonTeal}>
                        Generate Doc No.
                      </button>
                      <button type="button" onClick={() => setForm((current) => ({ ...current, documents: [...current.documents, emptyDocument(preferredPreparedBy)] }))} style={responsivePageButtonAddRow}>
                        + Row
                      </button>
                    </div>
                  </div>
                  {form.certificatePurpose === 'inspection' && <p style={{ color: '#475569', fontSize: 13 }}>Add one row for each drawing, inspection report or photo record. Enter the exact reference and revision in its name, then its date, page range and author. These references are saved with the certificate; this does not upload the source files.</p>}
                  <div style={{ display: 'grid', gap: 10 }}>
                    {form.documents.map((row, index) => (
                      <div key={`document-${index}`} style={{ display: 'grid', gap: 12, border: '1px solid #dbe4f0', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: documentHeaderColumns, gap: 10, alignItems: 'start' }}>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Section</div>
                            <select aria-label={`Document ${index + 1} section`} style={{ ...inputBase, padding: '8px 10px' }} value={row.section} onChange={(event) => updateDocument(index, 'section', event.target.value)}>
                              <option value="">Section</option>
                              {supportingDocumentSectionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                              {form.certificatePurpose === 'inspection' && ['Inspection report', 'Photo record'].map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                          </div>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Document name</div>
                            <input
                              style={{ ...inputBase, padding: '8px 10px', fontWeight: 600 }}
                              value={row.documentNo}
                              aria-label={`Document ${index + 1} name`}
                              onChange={(event) => updateDocument(index, 'documentNo', event.target.value)}
                              placeholder={getDocumentNamePlaceholder(row, form.matterType)}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: documentMetaColumns, gap: 10, alignItems: 'end' }}>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Date</div>
                            <input aria-label={`Document ${index + 1} date`} style={{ ...inputBase, padding: '8px 10px' }} type="date" value={row.documentDate} onChange={(event) => updateDocument(index, 'documentDate', event.target.value)} />
                          </div>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Document type</div>
                            <input aria-label={`Document ${index + 1} type`} style={{ ...inputBase, padding: '8px 10px', fontWeight: 600 }} value={row.documentType} onChange={(event) => updateDocument(index, 'documentType', event.target.value)} placeholder="Document type" />
                          </div>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Total pages</div>
                            <input aria-label={`Document ${index + 1} pages`} style={{ ...inputBase, padding: '8px 10px', fontWeight: 600 }} value={row.numberOfPages} onChange={(event) => updateDocument(index, 'numberOfPages', event.target.value)} placeholder="16 -> P1-16" />
                          </div>
                          <div>
                            <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Document by</div>
                            <input
                              style={{ ...inputBase, padding: '8px 10px' }}
                              list="certificate-prepared-by-options"
                              aria-label={`Document ${index + 1} author`}
                              value={row.preparedBy}
                              onChange={(event) => updateDocument(index, 'preparedBy', event.target.value)}
                              placeholder="Company"
                            />
                          </div>
                          <button type="button" onClick={() => setForm((current) => ({ ...current, documents: current.documents.filter((_, rowIndex) => rowIndex !== index).length ? current.documents.filter((_, rowIndex) => rowIndex !== index) : defaultDocumentRows(current.matterType, preferredPreparedBy) }))} style={responsivePageButtonDanger}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <datalist id="certificate-prepared-by-options">
                      {availablePreparedByOptions.map((option) => <option key={option} value={option} />)}
                    </datalist>
                    </div>
                  </div>
                </details>

                <details className="certificate-mobile-disclosure" open={isPhone ? undefined : true}>
                  <summary className="certificate-mobile-disclosure__summary">
                    {form.certificatePurpose === 'inspection' ? 'Provisions complied with' : 'Performance and standards'}
                  </summary>
                  <div className="certificate-mobile-disclosure__body">
                    <div style={{ padding: 18, borderBottom: '1px solid #dbe4f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: form.includePerformanceSolution ? 10 : 0 }}>
                    <div style={{ ...sheetLabel, marginBottom: 0 }}>Performance solution</div>
                    {form.includePerformanceSolution ? (
                      <button type="button" onClick={() => setForm((current) => ({ ...current, performanceRequirements: [...current.performanceRequirements, emptyPerformanceRequirement()] }))} style={responsivePageButtonAddRow}>
                        + Row
                      </button>
                    ) : null}
                  </div>
                  {form.includePerformanceSolution ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {form.performanceRequirements.map((row, index) => (
                        <div key={`pr-${index}`} style={{ display: 'grid', gridTemplateColumns: performanceRowColumns, gap: 10, border: '1px solid #dbe4f0', borderRadius: 12, padding: 12, background: '#fff' }}>
                          <div>
                            <input style={{ ...sheetInput, marginBottom: 8 }} value={row.relevantPerformanceRequirement} onChange={(event) => updatePerformanceRequirement(index, 'relevantPerformanceRequirement', event.target.value)} placeholder="Relevant performance requirement" />
                            <textarea style={sheetTextArea} value={row.details} onChange={(event) => updatePerformanceRequirement(index, 'details', event.target.value)} placeholder="Details" />
                          </div>
                          <button type="button" onClick={() => setForm((current) => ({ ...current, performanceRequirements: current.performanceRequirements.filter((_, rowIndex) => rowIndex !== index).length ? current.performanceRequirements.filter((_, rowIndex) => rowIndex !== index) : [emptyPerformanceRequirement()] }))} style={responsivePageButtonDanger}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                    </div>

                    <div style={{ padding: 18, borderBottom: '1px solid #dbe4f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: 10 }}>
                    <div style={{ ...sheetLabel, marginBottom: 0 }}>{form.certificatePurpose === 'inspection' ? 'Act, Regulation or NCC provisions' : 'Australian standards'}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {form.certificatePurpose !== 'inspection' && <button type="button" onClick={() => { void autoFillAustralianStandardsFromApi(); }} disabled={busy === 'Auto-filling Australian Standards...'} style={responsivePageButtonSecondary}>
                        AutoFill AS
                      </button>}
                    </div>
                  </div>
                  {form.certificatePurpose === 'inspection' && <CertificateProvisionHistory onSelect={(provision) => setForm((current) => ({ ...current, provisions: ensureProvisionValues(current.provisions, [provision]) }))} />}
                  {form.certificatePurpose === 'inspection' && <label style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                    <span>Provisions complied with (one per line)</span>
                    <textarea
                      aria-label="Provisions complied with (one per line)"
                      style={{ ...sheetTextArea, minHeight: 100 }}
                      value={form.provisions.filter((row) => !isAustralianStandardProvision(row.sectionProvision, availableAustralianStandardOptions)).map((row) => row.sectionProvision).join('\n')}
                      onChange={(event) => {
                        const text = event.target.value;
                        setForm((current) => ({
                          ...current,
                          provisions: [
                            ...text.split('\n').map((sectionProvision) => ({ sectionProvision })),
                            ...current.provisions.filter((row) => isAustralianStandardProvision(row.sectionProvision, availableAustralianStandardOptions)),
                          ],
                        }));
                      }}
                      placeholder="Identify the Act, Regulation or NCC and its section, regulation, part or performance requirement."
                    />
                    <span style={{ fontSize: 12, color: '#64748b' }}>Select Australian Standards using the checkboxes below.</span>
                  </label>}
                  <div style={{ display: 'grid', gridTemplateColumns: australianStandardsColumns, gap: 8, marginBottom: 10 }}>
                    <input
                      style={inputBase}
                      value={newAustralianStandard}
                      onChange={(event) => {
                        setNewAustralianStandard(event.target.value);
                        setAustralianStandardMessage('');
                      }}
                      placeholder="Add another Australian Standard option"
                    />
                    <button type="button" onClick={addAustralianStandardOption} style={responsivePageButtonAddRow}>
                      Save option
                    </button>
                  </div>
                  {australianStandardMessage ? (
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>{australianStandardMessage}</div>
                  ) : form.certificatePurpose === 'inspection' ? (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Enter the applicable Act, Regulation or NCC edition and provision above. Select additional Australian Standards below only where applicable. All entries are saved with this certificate; selecting an option does not confirm compliance.</div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                      {isStructuralMatter(form.matterType)
                        ? 'All available Australian Standards are shown below. AutoFill AS now follows the legacy structural certificate macro, including the matching NCC volume and the saved structural standards set.'
                        : 'All available Australian Standards are shown below. AutoFill AS now follows the legacy drainage certificate macro, including the drainage NCC volume and saved drainage standards set.'}
                    </div>
                  )}
                  <div style={{ display: 'grid', gap: 10 }}>
                    {availableAustralianStandardOptions.map((option) => {
                      return (
                        <label
                          key={option}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            border: '1px solid #dbe4f0',
                            borderRadius: 12,
                            padding: '12px 14px',
                            background: '#fff',
                            color: '#0f172a',
                            fontWeight: 600,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={hasProvisionValue(form.provisions, option)}
                            onChange={(event) => toggleAustralianStandard(option, event.target.checked)}
                          />
                          <span style={{ flex: 1 }}>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                    </div>
                  </div>
                </details>

                <div style={{ display: 'grid', gridTemplateColumns: staffStatusColumns, borderBottom: '1px solid #dbe4f0' }}>
                  <div style={{ padding: compactPanelPadding, borderRight: isPhone ? 'none' : '1px solid #dbe4f0', borderBottom: isPhone ? '1px solid #dbe4f0' : 'none' }}>
                    <div style={{ ...sheetLabel }}>Staff notes</div>
                    <textarea style={{ ...inputBase, minHeight: 120, background: '#fafafa', resize: 'vertical' }} value={form.notes} onChange={(event) => updateForm('notes', event.target.value)} placeholder="Working notes, reviewer notes, or issue instructions" />
                  </div>
                  <div style={{ padding: compactPanelPadding, background: '#f8fafc' }}>
                    <div style={{ ...sheetLabel }}>Draft status</div>
                    <div style={{ display: 'grid', gap: 10, fontSize: 14, color: '#334155' }}>
                      <div><strong>Certificate ID:</strong> {form.certificateId || 'Pending'}</div>
                      <div><strong>Stored draft:</strong> {storedDraftPdf?.fileName || 'Not rendered yet'}</div>
                      <div><strong>Draft updated:</strong> {formatDateLabel(storedDraftPdf?.createdDate || null)}</div>
                      <div><strong>SharePoint target:</strong> {getCertificateSharePointTargetLabel(form.matterType)}</div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: isPhone ? '14px' : '16px 18px', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', flexDirection: isPhone ? 'column' : 'row' }}>
                  <details style={{ minWidth: isPhone ? 0 : 280, width: isPhone ? '100%' : undefined, flex: isPhone ? undefined : '1 1 360px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#334155' }}>Advanced snapshot payload</summary>
                    <pre style={{ margin: '12px 0 0 0', padding: 14, borderRadius: 12, background: '#f8fafc', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>{advancedSnapshot}</pre>
                  </details>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', width: isPhone ? '100%' : undefined, flexDirection: stackedActionDirection }}>
                    <label style={{ ...certificateRenderStyleToggle, display: form.certificatePurpose === 'inspection' ? 'none' : undefined }}>
                      <input
                        type="checkbox"
                        checked={useProposedCertificateStyle}
                        onChange={(event) => {
                          setUseProposedCertificateStyle(event.target.checked);
                          setCertificateApproval(null);
                          setCertificateApprovalCode('');
                        }}
                      />
                      <span>Use proposed certificate layout</span>
                    </label>
                    <button type="button" onClick={requestResetForCreate} style={responsivePageButtonSecondary}>
                      New Cert
                    </button>
                    <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={previewCertificatePdf} loading={busy === 'preview-render'}>
                      Preview Cert126 PDF
                    </Button>
                    <button type="submit" disabled={busy === 'create' || busy === 'amend'} style={responsivePageButtonPrimary}>
                      {busy === 'create' || busy === 'amend' ? (mode === 'create' ? 'Saving...' : 'Updating...') : (mode === 'create' ? 'Save new certificate draft' : 'Save amendments + refresh draft PDF')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: lowerPanelColumns, gap: 18 }}>
          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 18, padding: 18 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Draft PDF</h2>
            <div style={{ display: 'grid', gap: 10, color: '#334155', fontSize: 14 }}>
              <div><strong>Certificate ID:</strong> {form.certificateId || 'Pending until save'}</div>
              <div><strong>Stored draft:</strong> {storedDraftPdf?.fileName || 'No blob-backed draft yet'}</div>
              <div><strong>Uploaded:</strong> {formatDateLabel(storedDraftPdf?.createdDate || null)}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14, flexDirection: stackedActionDirection }}>
              <label style={{ ...certificateRenderStyleToggle, display: form.certificatePurpose === 'inspection' ? 'none' : undefined }}>
                <input
                  type="checkbox"
                  checked={useProposedCertificateStyle}
                  onChange={(event) => {
                    setUseProposedCertificateStyle(event.target.checked);
                    setCertificateApproval(null);
                    setCertificateApprovalCode('');
                  }}
                />
                <span>Use proposed certificate layout</span>
              </label>
              <Button type="button" size="sm" style={responsivePageButtonPrimary} onClick={async () => {
                clearFeedback();
                if (!form.certificateId.trim()) {
                  setError('Save the certificate first so the draft PDF can be linked to a certificate ID.');
                  return;
                }
                setBusy('render');
                try {
                  await renderStoreAndOpenDraftPdf(form.certificateId);
                  setSuccess(`Rendered and stored draft PDF for certificate ${form.certificateNumber || form.certificateId}.`);
                } catch (err: any) {
                  setError(formatUserFacingError(err, 'Certificate draft PDF could not be rendered and stored.'));
                } finally {
                  setBusy(null);
                }
              }} loading={busy === 'render'}>
                Render + store draft PDF
              </Button>
              <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={reviewStoredDraftPdf} loading={busy === 'review-draft'}>
                Review stored draft
              </Button>
              <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={saveStoredDraftPdfToSharePoint} loading={busy === 'draft-sharepoint'} disabled={!storedDraftPdf?.draftFileId}>
                Save draft to SharePoint
              </Button>
            </div>
            {result?.workflow === 'certificate-draft-pdf-sharepoint' ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#eff6ff', color: '#1e3a8a', fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>SharePoint path:</strong> {result?.sharePointUpload?.plannedPath || result?.data?.sharePointUpload?.plannedPath || 'Not returned'}</div>
                <div><strong>File name:</strong> {result?.sharePointUpload?.fileName || result?.data?.sharePointUpload?.fileName || 'Not returned'}</div>
              </div>
            ) : null}
          </div>

          <div id="certificate-confirm" style={{ background: '#fff', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 18, padding: 18 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Confirm Issue</h2>
            <form onSubmit={confirmAndIssue} style={{ display: 'grid', gap: 12 }}>
              <input style={inputBase} value={confirmForm.certificateId} onChange={(event) => setConfirmForm((current) => ({ ...current, certificateId: event.target.value }))} placeholder="Certificate ID" />
              <input style={inputBase} type="file" accept="application/pdf,.pdf" onChange={(event) => setConfirmForm((current) => ({ ...current, pdfFile: event.target.files?.[0] || null }))} />
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                Confirming uses the stored draft PDF by default and copies it into the SharePoint construction package folder. Upload a replacement PDF only if staff want to override the stored draft at confirm time.
              </div>
              <div style={{ borderRadius: 12, border: '1px solid #d8e1f2', background: '#fbfdff', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #d8e1f2' }}>
                  <span style={{ width: 4, height: 28, borderRadius: 999, background: '#3156c7', flexShrink: 0 }} />
                  <div style={{ fontWeight: 900, color: '#061833', fontSize: 16 }}>Digital Signing Approval</div>
                </div>
                <div style={{ display: 'grid', gap: 12, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: isPhone ? 'flex-start' : 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800, color: '#0b2545', minWidth: 76 }}>Status</div>
                    <div style={{
                      borderRadius: 999,
                      border: certificateApprovalIsApproved ? '1px solid #b7d8c9' : '1px solid #cbd5e1',
                      background: certificateApprovalIsApproved ? '#e8f5ee' : '#f8fafc',
                      color: certificateApprovalIsApproved ? '#064e3b' : '#475569',
                      padding: '5px 12px',
                      fontSize: 12,
                      lineHeight: 1.2,
                      textAlign: 'center',
                      fontWeight: 900,
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}>
                      {certificateApprovalStatusLabel}
                    </div>
                    <div style={{ color: '#53627a', fontSize: 13, lineHeight: 1.45, flex: '1 1 220px', minWidth: isPhone ? '100%' : 180 }}>
                      {certificateApprovalStatusMessage}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 8, borderTop: '1px solid #e1e7f2', paddingTop: 12 }}>
                    <div style={{ fontWeight: 800, color: '#0b2545' }}>Email OTP</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', flexDirection: 'row', alignItems: 'center', width: '100%', overflowX: 'auto', paddingBottom: 1 }}>
                      {certificateApproval?.approvalId && certificateApproval.status !== 'Approved' ? (
                        <>
                          <input
                            aria-label="Certificate approval OTP"
                            style={{ ...inputBase, width: 'clamp(84px, 32vw, 150px)', minWidth: 84, maxWidth: 150, flex: '1 1 150px', letterSpacing: '0.18em', fontWeight: 800, textAlign: 'center' }}
                            value={certificateApprovalCode}
                            onChange={(event) => setCertificateApprovalCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            inputMode="numeric"
                          />
                          <Button
                            type="button"
                            size="sm"
                            style={{ ...responsivePageButtonSuccess, width: 'auto', flexShrink: 0 }}
                            onClick={() => { void verifyCertificateIssueApproval(); }}
                            loading={busy === 'issue-approval-verify'}
                            disabled={certificateApprovalCode.trim().length < 6}
                          >
                            Verify OTP
                          </Button>
                        </>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        style={{ ...responsivePageButtonSecondary, width: 'auto', flexShrink: 0 }}
                        onClick={() => { void requestCertificateIssueApproval(); }}
                        loading={busy === 'issue-approval'}
                      >
                        {certificateApproval?.approvalId ? 'Request new OTP' : 'Request OTP'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={busy === 'issue' || certificateApproval?.status !== 'Approved'} style={responsivePageButtonSuccess}>
                {busy === 'issue' ? 'Confirming...' : 'Confirm digitally signed certificate'}
              </button>
            </form>
            {result?.workflow === 'certificate-issue-lock' ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#ecfdf5', color: '#166534', fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>SharePoint path:</strong> {result?.data?.sharePointUpload?.plannedPath || result?.sharePointUpload?.plannedPath || 'Not returned'}</div>
                <div><strong>PDF source:</strong> {result?.data?.pdfSource || result?.pdfSource || 'Rendered'}</div>
              </div>
            ) : null}
          </div>

          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 18, padding: 18 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Version History</h2>
            <div style={{ display: 'grid', gap: 12 }}>
              <CertificateHistoryLookup
                certificateId={historyLookupId}
                loading={busy === 'versions'}
                onSelect={loadVersions}
                onQueryChange={() => setVersions(null)}
              />

              {versions && versions.length > 0 ? (
                <div style={{ display: 'grid', gap: 10, maxHeight: 420, overflow: 'auto' }}>
                  {versions.map((version, index) => {
                    const versionNumber = renderHistoryValue(version, ['VersionNo', 'versionNo'], 'Not set');
                    const revision = renderHistoryValue(version, ['VersionTag', 'versionTag'], '');
                    const recordId = renderHistoryValue(version, ['CertificateId', 'certificateId'], historyLookupId);
                    const versionStatus = renderHistoryValue(version, ['StatusSnapshot', 'Status', 'status'], version.IsLocked ? 'Locked' : 'Draft');
                    const lockedState = Number(version?.IsLocked ?? version?.isLocked ?? 0) === 1 ? 'Locked' : 'Working';
                    return (
                      <div key={`${versionNumber}-${index}`} style={{ border: '1px solid #dbe4f0', borderRadius: 14, padding: 14, background: '#f8fafc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{revision ? `Revision ${revision}` : `Version ${versionNumber}`}</div>
                            <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>Version {versionNumber} · Certificate record {recordId}</div>
                            <div style={{ marginTop: 4, color: '#475569', lineHeight: 1.5 }}>{renderHistoryValue(version, ['CertificateNumber', 'certificateNumber'], form.certificateNumber || 'Certificate')}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <StatusBadge status={versionStatus} />
                            <StatusBadge status={lockedState} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12, fontSize: 13, color: '#334155' }}>
                          <div><strong>Issue date:</strong> {renderHistoryValue(version, ['DateIssued', 'IssueDate', 'issueDate'])}</div>
                          <div><strong>File:</strong> {renderHistoryValue(version, ['FileName', 'fileName'])}</div>
                          <div><strong>Updated:</strong> {formatDateLabel(renderHistoryValue(version, ['ModifiedDate', 'CreatedDate', 'createdDate'], ''))}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : versions ? (
                <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', color: '#475569' }}>No versions found for this certificate ID yet.</div>
              ) : (
                <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', color: '#475569' }}>Search by certificate ID, certificate number or project number, then choose a certificate to review its history.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={drawingDocNoGeneratorOpen}
        onClose={() => {
          setDrawingDocNoGeneratorOpen(false);
          setDrawingDocNoMessage('');
        }}
        title="Generate Drawing Doc No."
        size="xl"
        className="wdgf-drawing-doc-no-modal"
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%', flexWrap: 'wrap', flexDirection: isPhone ? 'column' : 'row' }}>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, maxWidth: 520 }}>
              Click one revision per sheet row. Checked sheets are included in the generated doc number string.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flexDirection: stackedActionDirection }}>
              <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={() => {
                setDrawingDocNoGeneratorOpen(false);
                setDrawingDocNoMessage('');
              }}>
                Cancel
              </Button>
              <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={() => { void copyGeneratedDrawingDocNo(); }}>
                Copy
              </Button>
              <Button type="button" size="sm" style={responsivePageButtonPrimary} onClick={() => { void applyGeneratedDrawingDocNo(true); }}>
                OK
              </Button>
            </div>
          </div>
        )}
      >
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: modalColumns, gap: 16 }}>
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ border: '1px solid #dbe4f0', borderRadius: 16, padding: 16, background: '#f8fbff', display: 'grid', gap: 12 }}>
                <div style={{ ...sheetLabel, marginBottom: 0 }}>Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: modalSettingsColumns, gap: 10 }}>
                  <div>
                    <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Drawing prefix</div>
                    <input
                      style={{ ...inputBase, padding: '8px 10px', fontWeight: 700 }}
                      value={drawingDocNoSettingsDraft.prefix}
                      maxLength={2}
                      onChange={(event) => {
                        setDrawingDocNoSettingsDraft((current) => ({ ...current, prefix: event.target.value.toUpperCase() }));
                        setDrawingDocNoMessage('');
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Total pages</div>
                    <input
                      style={{ ...inputBase, padding: '8px 10px', fontWeight: 700 }}
                      value={drawingDocNoSettingsDraft.totalPages}
                      onChange={(event) => {
                        setDrawingDocNoSettingsDraft((current) => ({ ...current, totalPages: event.target.value }));
                        setDrawingDocNoMessage('');
                      }}
                      placeholder="16"
                    />
                  </div>
                </div>

                <div>
                  <div style={{ ...sheetLabel, marginBottom: 6, fontSize: 10 }}>Revisions (custom sequencing)</div>
                  <textarea
                    style={{ ...inputBase, minHeight: 82, resize: 'vertical', lineHeight: 1.45 }}
                    value={drawingDocNoSettingsDraft.revisionSequenceInput}
                    onChange={(event) => {
                      setDrawingDocNoSettingsDraft((current) => ({ ...current, revisionSequenceInput: event.target.value }));
                      setDrawingDocNoMessage('');
                    }}
                    placeholder="A,B,C,D,E,0,1,2,3..."
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                    Structural default: <code>{structuralRevisionSequenceDefault}</code>. Civil / drainage default: <code>{civilRevisionSequenceDefault}</code>.
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flexDirection: stackedActionDirection }}>
                  <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={() => refreshDrawingDocNoMatrix(drawingDocNoSettingsDraft, drawingDocNoSelections)}>
                    Refresh Table
                  </Button>
                  <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={() => {
                    const defaults = normalizeDrawingDocNoSettings({
                      prefix: getDefaultDrawingPrefix(form.matterType),
                      totalPages: drawingDocNoSettingsDraft.totalPages,
                      revisionSequenceInput: getDefaultRevisionSequenceInput(form.matterType),
                    });
                    setDrawingDocNoSettingsDraft(defaults);
                    refreshDrawingDocNoMatrix(defaults);
                  }}>
                    Reset Defaults
                  </Button>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: isPhone ? 'stretch' : 'center', flexDirection: isPhone ? 'column' : 'row', marginBottom: 6 }}>
                    <div style={{ ...sheetLabel, marginBottom: 0, fontSize: 10 }}>Generated doc no.</div>
                    <Button type="button" size="sm" variant="secondary" style={responsivePageButtonSecondary} onClick={() => { void copyGeneratedDrawingDocNo(); }}>
                      Copy
                    </Button>
                  </div>
                  <textarea
                    style={{ ...inputBase, minHeight: 88, resize: 'vertical', lineHeight: 1.5, fontWeight: 600, background: '#fff' }}
                    readOnly
                    value={generatedDrawingDocNo}
                    placeholder="Project no. - S01[A], S02[B], ..."
                  />
                  {drawingDocNoMessage ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>{drawingDocNoMessage}</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div style={{ border: '1px solid #dbe4f0', borderRadius: 16, overflow: 'hidden', background: '#fff' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #dbe4f0', background: '#f8fafc' }}>
                <div style={{ ...sheetLabel, marginBottom: 0 }}>Revision Matrix</div>
              </div>
              <div style={{ overflow: 'auto', maxHeight: 480 }}>
                {drawingDocNoSheetCodes.length > 0 && drawingDocNoRevisionTokens.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: Math.max(560, 180 + (drawingDocNoRevisionTokens.length * 70)) }}>
                    <thead>
                      <tr>
                        <th style={{ ...tableHeaderCell, position: 'sticky', left: 0, top: 0, zIndex: 4, textAlign: 'left', minWidth: 150 }}>Sheet</th>
                        {drawingDocNoRevisionTokens.map((revision) => (
                          <th key={revision} style={{ ...tableHeaderCell, position: 'sticky', top: 0, zIndex: 3, textAlign: 'center', minWidth: 68 }}>
                            {revision}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {drawingDocNoSheetCodes.map((sheetCode) => (
                        <tr key={sheetCode}>
                          <td style={{ ...tableCell, position: 'sticky', left: 0, zIndex: 2, minWidth: 150, fontWeight: 700, boxShadow: '8px 0 12px -12px rgba(15, 23, 42, 0.65)' }}>{sheetCode}</td>
                          {drawingDocNoRevisionTokens.map((revision) => (
                            <td key={`${sheetCode}-${revision}`} style={{ ...tableCell, textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={drawingDocNoSelections[sheetCode] === revision}
                                onChange={() => updateDrawingDocNoSelection(sheetCode, revision)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: 18, color: '#475569' }}>Enter at least one revision token and a positive total page count, then refresh the table.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CertificateWorkflowPage;
